import React, { useState, useCallback, forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '../../../theme/ThemeProvider';
import { useAuth } from '../../Auth/AuthContext';
import { FirebaseService } from '../../../services/FirebaseService';
import { useClientPDFExport } from '../../../hooks/useClientPDFExport';
import FormattingToolbar from './FormattingToolbar';
import './SimpleDocumentEditor.css';
import {
  insertImageAtCursor,
  showResizeHandles,
  hideResizeHandles,
  isImageElement,
  getImageElementHeight,
  initializeAllImageContextMenus,
  rebindAllImageOverlays
} from './imageUtils';

// TypeScript declarations for caret position functions
declare global {
  interface Document {
    caretPositionFromPoint(x: number, y: number): { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint(x: number, y: number): Range | null;
  }
}

// Step 2: Overlay position data interface
export interface OverlayPositionData {
  id: string;
  metadata: any;
  rect: DOMRect;
}

interface SimpleDocumentEditorProps {
  width?: number;
  height?: number;
  reportId?: string;
  reportTitle?: string;
  readOnly?: boolean;
  onSaveStatusChange?: (status: 'saved' | 'saving' | 'unsaved') => void;
  isSharedReport?: boolean;
  onOverlayPositionsChange?: (positions: OverlayPositionData[]) => void;
}

export interface SimpleDocumentEditorHandle {
  exportDocumentData: (title?: string) => Promise<{
    title: string;
    reportId: string;
    pages: any[];
  }>;
  resetToBlankDocument: () => void;
  handleClientSideExport: () => Promise<void>;
  createFromTemplate: (entries: any[]) => void;
  updateOverlayPositions: () => void; // Force overlay position re-extraction
  // Export progress and status
  getExportProgress: () => number;
  isExporting: () => boolean;
}

// Constants for Google Docs-style page layout
const PAGE_WIDTH = 595; // A4 width in pixels
const PAGE_HEIGHT = 842; // A4 height in pixels
const PAGE_MARGIN = 54; // Standard A4 margins
const PAGE_CONTENT_HEIGHT = PAGE_HEIGHT - (PAGE_MARGIN * 2); // 734px available content area

// Constants for image handling
const STANDARD_DRAG_IMAGE_WIDTH = 128; // Standard width for all dragged images (reduced by 5px)

/**
 * Adjust range boundaries to prevent splitting image containers
 * Critical fix for Enter key behavior with overlay system
 */
const adjustRangeForImageContainers = (originalRange: Range): Range => {
  const range = originalRange.cloneRange();
  
  // Check if range crosses any image containers
  const commonAncestor = range.commonAncestorContainer;
  
  // Find all image containers within the range
  let container: Node | null = commonAncestor;
  
  // Walk up to find the paragraph containing our range
  while (container && 
         (container.nodeType !== Node.ELEMENT_NODE || 
          (container as Element).tagName !== 'P')) {
    container = container.parentNode;
  }
  
  if (!container) return range;
  
  // Find all image containers in the paragraph
  const imageContainers = (container as Element).querySelectorAll('.inline-image-container');
  
  for (let i = 0; i < imageContainers.length; i++) {
    const imgContainer = imageContainers[i];
    
    // Check if range intersects with this container
    const containerRange = document.createRange();
    containerRange.selectNodeContents(imgContainer);
    
    // If our range would split this container, adjust it
    try {
      if (range.compareBoundaryPoints(Range.START_TO_END, containerRange) === 1 && 
          range.compareBoundaryPoints(Range.END_TO_START, containerRange) === 1) {
        
        // Range crosses the container - adjust to not split it
        if (range.startContainer === imgContainer || imgContainer.contains(range.startContainer)) {
          // Start is inside container, move start to after container
          try {
            range.setStartAfter(imgContainer);
          } catch (error) {
            console.warn('🖼️ Failed to setStartAfter for image container:', error);
            // Skip this adjustment if it fails
          }
        }
        
        if (range.endContainer === imgContainer || imgContainer.contains(range.endContainer)) {
          // End is inside container, move end to before container
          try {
            range.setEndBefore(imgContainer);
          } catch (error) {
            console.warn('🖼️ Failed to setEndBefore for image container:', error);
            // Skip this adjustment if it fails
          }
        }
      }
    } catch (error) {
      console.warn('🖼️ Range comparison failed for image container:', error);
      // Continue with next container
      continue;
    }
  }
  
  return range;
};

const SimpleDocumentEditor = forwardRef<SimpleDocumentEditorHandle, SimpleDocumentEditorProps>(({
  width = PAGE_WIDTH,
  height = PAGE_HEIGHT,
  reportId,
  reportTitle,
  readOnly = false,
  onSaveStatusChange,
  isSharedReport = false,
  onOverlayPositionsChange
}, ref) => {
  const theme = useTheme();
  const { user } = useAuth();

  // ================== PAGINATED GOOGLE DOCS ARCHITECTURE ==================
  // REAL page containers inside contentEditable - NO MORE CURSOR DRIFT!

  // Core editor state - ONE single HTML content string
  const [editorHTML, setEditorHTML] = useState<string>('<p><br></p>');
  const [isLoading, setIsLoading] = useState(true);

  // Refs for the single editor and hidden measurement container
  const editorRootRef = useRef<HTMLDivElement | null>(null);
  const measureContainerRef = useRef<HTMLDivElement | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Image interaction state
  const isResizingRef = useRef(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Visual PDF export hook - captures exact visual appearance
  const { exportPDF, status: exportStatus, progress: exportProgress } = useClientPDFExport(editorRootRef as React.RefObject<HTMLElement>, {
    onProgress: (progress: number) => {
      //console.log(`📈 Visual export progress: ${progress.toFixed(1)}%`);
    },
    onComplete: (pdfBlob: Blob) => {
      // PDF export completed
    },
    onError: (error: Error) => {
      //console.error('❌ Visual PDF export failed:', error);
    }
  });

  // Export status for display - using both reactive state and ref for different purposes
  const isExporting = exportStatus === 'exporting';
  const isExportingRef = useRef(false); // Non-reactive flag to prevent re-renders during export
  const allowOneMoreOverlayUpdate = useRef(false); // Allow one final overlay update before export

  // ================== PAGINATION CONTROL ==================

  // Control flags for pagination behavior
  const isInitialLoad = useRef(true);
  const updateTimeout = useRef<NodeJS.Timeout | null>(null);
  const skipNextPaginationUpdate = useRef(false);
  const lastPaginationTime = useRef(0);
  const PAGINATION_THROTTLE = 1000; // 1 second minimum between pagination updates

  // 🔧 ENTER KEY RACE CONDITION FIX
  const isEnterKeyProcessing = useRef(false);
  const enterKeyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ================== ADVANCED GOOGLE DOCS PAGINATION ENGINE ==================

  /**
   * Enhanced cursor position tracking with precise node path
   */
  // interface CursorPosition {
  //   nodePath: number[];  // Path to text node from root
  //   offset: number;      // Character offset within text node
  //   pageIndex?: number;  // Which visual page cursor should be on
  // }

  /**
   * Split result for content that exceeds page boundaries
   */
  // interface SplitResult {
  //   beforeSplit: string;  // Content that fits on current page
  //   afterSplit: string;   // Overflow content for next page
  //   splitOccurred: boolean;
  // }

  /**
   * Updates text content of element while preserving formatting spans
   */
  // const updateElementTextContent = useCallback((element: HTMLElement, newText: string) => {
  //   const walker = document.createTreeWalker(
  //     element,
  //     NodeFilter.SHOW_TEXT,
  //     null
  //   );
  //   
  //   let textNode = walker.nextNode() as Text;
  //   let remainingText = newText;
  //   
  //   while (textNode && remainingText.length > 0) {
  //     const nodeLength = Math.min(textNode.textContent?.length || 0, remainingText.length);
  //     textNode.textContent = remainingText.substring(0, nodeLength);
  //     remainingText = remainingText.substring(nodeLength);
  //     textNode = walker.nextNode() as Text;
  //   }
  //   
  //   // Remove remaining text nodes
  //   while (textNode) {
  //     const nextNode = walker.nextNode() as Text;
  //     textNode.textContent = '';
  //     textNode = nextNode;
  //   }
  // }, []);

  /**
   * Measures inline words and their cumulative heights within a block element
   * Returns array of {word, cumulativeHeight} to determine exact split points
   */
  // Commented out unused function to fix compilation warnings
  // const measureInlineWords = useCallback((blockElement: HTMLElement): Array<{word: string, height: number, cumulativeHeight: number}> => {
  //   if (!measureContainerRef.current) return [];
  //   
  //   const words: Array<{word: string, height: number, cumulativeHeight: number}> = [];
  //   const textContent = blockElement.textContent || '';
  //   const wordArray = textContent.split(/(\s+)/); // Preserve whitespace
  //   
  //   // Clone the element for measurement
  //   const testElement = blockElement.cloneNode(true) as HTMLElement;
  //   measureContainerRef.current.appendChild(testElement);
  //   
  //   let cumulativeHeight = 0;
  //   let currentText = '';
  //   
  //   for (const word of wordArray) {
  //     currentText += word;
  //     testElement.innerHTML = currentText;
  //     
  //     const currentHeight = testElement.clientHeight || testElement.offsetHeight || 20;
  //     const wordHeight = currentHeight - cumulativeHeight;
  //     
  //     words.push({
  //       word,
  //       height: wordHeight,
  //       cumulativeHeight: currentHeight
  //     });
  //     
  //     cumulativeHeight = currentHeight;
  //   }
  //   
  //   // Cleanup
  //   measureContainerRef.current.removeChild(testElement);
  //   
  //   return words;
  // }, []);

  /**
   * Helper function to safely append/remove DOM elements during export
   */
  const safeAppendChild = useCallback((parent: HTMLElement, child: HTMLElement): boolean => {
    if (isExportingRef.current) {
      //console.log('⏸️ Skipping DOM appendChild - export in progress');
      return false;
    }
    try {
      parent.appendChild(child);
      return true;
    } catch (error) {
      console.warn('⚠️ Safe appendChild failed:', error);
      return false;
    }
  }, []); // No dependencies needed since isExportingRef is a ref

  const safeRemoveChild = useCallback((parent: HTMLElement, child: HTMLElement): boolean => {
    if (isExportingRef.current) {
      //console.log('⏸️ Skipping DOM removeChild - export in progress');
      return false;
    }
    try {
      // Additional safety checks
      if (!child || !parent) {
        console.warn('⚠️ Safe removeChild: null parent or child');
        return false;
      }
      if (!document.body.contains(child)) {
        //console.log('ℹ️ Child already removed from DOM');
        return true; // Not an error, just already removed
      }
      if (child.parentNode === parent) {
        parent.removeChild(child);
        return true;
      } else {
        console.warn('⚠️ Child parent mismatch - child may have been moved');
        return false;
      }
    } catch (error) {
      console.warn('⚠️ Safe removeChild failed:', error);
      return false;
    }
  }, []); // No dependencies needed since isExportingRef is a ref

  /**
   * Split paragraph into smaller chunks that fit within page height
   */
  const splitParagraphByLines = useCallback((paragraph: HTMLParagraphElement): Array<{ html: string, height: number }> => {
    const text = paragraph.textContent || '';
    const words = text.split(/\s+/).filter(word => word.length > 0);

    if (words.length === 0) {
      return [{ html: '<p><br></p>', height: 19 }]; // Approximate line height
    }

    const parts: Array<{ html: string, height: number }> = [];
    let currentWords: string[] = [];

    for (const word of words) {
      const testWords = [...currentWords, word];
      const testHtml = `<p style="margin: 0; line-height: 1.6;">${testWords.join(' ')}</p>`;

      // Measure test content
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = `
        position: absolute;
        left: -9999px;
        width: ${PAGE_WIDTH - (PAGE_MARGIN * 2)}px;
        font-family: "Times New Roman", serif;
        font-size: 12px;
        line-height: 1.6;
        visibility: hidden;
      `;
      tempDiv.innerHTML = testHtml;
      if (!safeAppendChild(document.body, tempDiv)) {
        // If DOM operations are blocked, return current progress
        if (currentWords.length > 0) {
          parts.push({ html: `<p style="margin: 0; line-height: 1.6;">${currentWords.join(' ')}</p>`, height: 19 });
        }
        return parts;
      }

      const height = tempDiv.scrollHeight;
      safeRemoveChild(document.body, tempDiv);

      if (height > PAGE_CONTENT_HEIGHT && currentWords.length > 0) {
        // Add current words as a part
        const partHtml = `<p style="margin: 0; line-height: 1.6;">${currentWords.join(' ')}</p>`;

        // Measure the actual part height
        const partDiv = document.createElement('div');
        partDiv.style.cssText = tempDiv.style.cssText;
        partDiv.innerHTML = partHtml;
        if (!safeAppendChild(document.body, partDiv)) {
          // If DOM operations are blocked, use estimated height
          parts.push({ html: partHtml, height: 19 });
        } else {
          const partHeight = partDiv.scrollHeight;
          safeRemoveChild(document.body, partDiv);
          parts.push({ html: partHtml, height: partHeight });
        }

        // Start new part with current word
        currentWords = [word];
      } else {
        currentWords = testWords;
      }
    }

    // Add remaining words
    if (currentWords.length > 0) {
      const partHtml = `<p style="margin: 0; line-height: 1.6;">${currentWords.join(' ')}</p>`;

      // Measure final part
      const partDiv = document.createElement('div');
      partDiv.style.cssText = `
        position: absolute;
        left: -9999px;
        width: ${PAGE_WIDTH - (PAGE_MARGIN * 2)}px;
        font-family: "Times New Roman", serif;
        font-size: 12px;
        line-height: 1.6;
        visibility: hidden;
      `;
      partDiv.innerHTML = partHtml;
      if (!safeAppendChild(document.body, partDiv)) {
        // If DOM operations are blocked, use estimated height
        parts.push({ html: partHtml, height: 19 });
      } else {
        const partHeight = partDiv.scrollHeight;
        safeRemoveChild(document.body, partDiv);
        parts.push({ html: partHtml, height: partHeight });
      }
    }

    return parts;
  }, [safeAppendChild, safeRemoveChild]);

  /**
   * Ensure measurement container is properly initialized and ready for layout calculations
   * This prevents the race condition where pagination runs before measurement setup is complete
   */
  const ensureMeasurementContainerReady = useCallback((): boolean => {
    // Step 1: Check if container exists and is attached to DOM
    if (!measureContainerRef.current || !document.body.contains(measureContainerRef.current)) {
      const measureDiv = document.createElement('div');
      measureDiv.style.cssText = `
        position: absolute;
        left: -9999px;
        top: -9999px;
        width: ${PAGE_WIDTH - (PAGE_MARGIN * 2)}px;
        font-family: "Times New Roman", serif;
        font-size: 12px;
        line-height: 1.6;
        color: #333333;
        visibility: hidden;
        overflow: visible;
        white-space: normal;
        word-wrap: break-word;
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      `;
      measureDiv.id = 'measure-container';
      if (safeAppendChild(document.body, measureDiv)) {
        measureContainerRef.current = measureDiv;
      } else {
        //console.log('⚠️ Could not create measurement container during export');
      }
    }

    // Step 2: Force layout calculation to ensure container is ready
    if (measureContainerRef.current) {
      void measureContainerRef.current.offsetHeight; // Force reflow
      return true;
    }

    return false;
  }, [safeAppendChild]);

  /**
   * Wait for all images in container to load their natural dimensions
   * This prevents height calculation errors during initial pagination
   */
  const waitForImagesLoaded = useCallback(async (container: HTMLElement): Promise<void> => {
    const images = container.querySelectorAll('img');
    if (images.length === 0) return;

    const imagePromises = Array.from(images).map((img) => {
      return new Promise<void>((resolve) => {
        const imageElement = img as HTMLImageElement;
        
        // If image is already loaded, resolve immediately
        if (imageElement.complete && imageElement.naturalHeight !== 0) {
          resolve();
          return;
        }

        // Set up load handlers with timeout fallback
        const timeout = setTimeout(() => {
          resolve(); // Continue even if image fails to load
        }, 3000);

        imageElement.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        imageElement.onerror = () => {
          clearTimeout(timeout);
          resolve(); // Continue even if image fails
        };

        // Force reload if src is set but not loaded
        if (imageElement.src && !imageElement.complete) {
          imageElement.src = imageElement.src; // Trigger reload
        }
      });
    });

    await Promise.all(imagePromises);
  }, []);

  /**
   * Accurate pagination with precise line-level overflow detection
   * Returns array of HTML strings representing each page
   * 
   * ENHANCED with comprehensive debugging and image loading detection
   */
  const computePagination = useCallback((htmlContent: string): string[] => {
    const debugId = Date.now(); // Unique ID for this pagination run
    //console.log(`🔬 PAGINATION START [${debugId}]: Content ${htmlContent.length} chars`);
    
    // CRITICAL: Ensure measurement infrastructure is ready before proceeding
    if (!ensureMeasurementContainerReady()) {
      console.warn('🚨 Measurement container failed to initialize - returning single page');
      return [htmlContent || '<p><br></p>'];
    }

    if (!htmlContent || htmlContent.trim() === '') {
      //console.log(`🔬 PAGINATION END [${debugId}]: Empty content`);
      return ['<p><br></p>'];
    }
    
    // 🔍 CRITICAL FIX: Check image loading status from the actual editor (not clones)
    // With pre-loading in place, this should rarely trigger, but kept as safety check
    let imagesNotLoaded = 0;
    let totalImages = 0;
    
    if (editorRootRef.current) {
      const actualImages = editorRootRef.current.querySelectorAll('img');
      totalImages = actualImages.length;
      
      actualImages.forEach(img => {
        const imageEl = img as HTMLImageElement;
        if (!imageEl.complete || imageEl.naturalHeight === 0) {
          imagesNotLoaded++;
        }
      });
    }
    
    if (imagesNotLoaded > 0) {
      //console.log(`⚠️ IMAGE LOADING ISSUE [${debugId}]: ${imagesNotLoaded}/${totalImages} images not loaded in editor - will use fallback dimensions and continue pagination`);
      // DON'T return early - continue with pagination using fallback/editor dimensions
      // The code below already handles unloaded images by checking the editor's actual images
    } else if (totalImages > 0) {
      //console.log(`✅ ALL IMAGES LOADED [${debugId}]: ${totalImages} images ready for accurate measurement`);
    }

    // 🔍 COMPREHENSIVE DEBUG: Analyze input content
    const hasImages = htmlContent.includes('<img');
    const elementCount = (htmlContent.match(/<[^>]+>/g) || []).length;
    const imageCount = (htmlContent.match(/<img[^>]*>/g) || []).length;
    const paragraphCount = (htmlContent.match(/<p[^>]*>/g) || []).length;
    /*console.log(`🔬 CONTENT ANALYSIS [${debugId}]:`, {
      elementCount,
      imageCount,
      paragraphCount,
      hasImages,
      contentLength: htmlContent.length,
      firstChar: htmlContent[0],
      lastChar: htmlContent[htmlContent.length - 1]
    });*/

    const measureContainer = measureContainerRef.current!; // Safe after ensureMeasurementContainerReady()
    
    // 🔍 DEBUG: Log measurement container state BEFORE reset
    /*console.log(`🔬 BEFORE RESET [${debugId}]:`, {
      containerExists: !!measureContainer,
      containerInDOM: document.body.contains(measureContainer),
      containerHTML: measureContainer.innerHTML.substring(0, 100) + '...',
      containerWidth: measureContainer.style.width,
      containerHeight: measureContainer.offsetHeight,
      containerChildren: measureContainer.children.length
    });*/
    
    // CRITICAL: Completely reset measurement container to ensure clean state - safely
    if (!isExportingRef.current) {
      try {
        measureContainer.remove(); // Remove from DOM completely
      } catch (error) {
        //('ℹ️ Measurement container already removed during reset');
      }
    } else {
      //console.log('⏸️ Skipping measurement container reset - export in progress');
      // Return single page with current content if measurement reset is blocked
      return [htmlContent || '<p><br></p>'];
    }
    
    // Recreate fresh measurement container
    const freshContainer = document.createElement('div');
    freshContainer.style.cssText = `
      position: absolute;
      left: -9999px;
      top: -9999px;
      width: ${PAGE_WIDTH - (PAGE_MARGIN * 2)}px;
      font-family: "Times New Roman", serif;
      font-size: 12px;
      line-height: 1.6;
      color: #333333;
      visibility: hidden;
      overflow: visible;
      white-space: normal;
      word-wrap: break-word;
      margin: 0;
      padding: 0;
      display: block;
    `;
    freshContainer.id = 'measure-container';
    if (safeAppendChild(document.body, freshContainer)) {
      measureContainerRef.current = freshContainer;
    } else {
      //console.log('⚠️ Could not recreate fresh measurement container during export');
      return [htmlContent || '<p><br></p>']; // Return single page if DOM operations are blocked
    }
    
    // 🔍 DEBUG: Verify fresh container setup
    /*console.log(`🔬 FRESH CONTAINER [${debugId}]:`, {
      width: freshContainer.style.width,
      inDOM: document.body.contains(freshContainer),
      offsetHeight: freshContainer.offsetHeight,
      scrollHeight: freshContainer.scrollHeight,
      computedWidth: window.getComputedStyle(freshContainer).width
    });*/
    
    // Force layout calculation on fresh container
    void freshContainer.offsetHeight;

    // Now set the content for measurement
    const pages: string[] = [];

    // Parse content into individual elements using fresh container
    const beforeHTML = performance.now();
    freshContainer.innerHTML = htmlContent;
    const afterHTML = performance.now();
    
    // 🔍 DEBUG: Timing and state after innerHTML
    /*console.log(`🔬 AFTER innerHTML [${debugId}]:`, {
      setTime: (afterHTML - beforeHTML).toFixed(2) + 'ms',
      containerHeight: freshContainer.offsetHeight,
      containerScrollHeight: freshContainer.scrollHeight,
      childrenCount: freshContainer.children.length,
      hasImages: freshContainer.querySelectorAll('img').length
    });*/
    
    // ⚠️ CRITICAL: Force layout after innerHTML change
    void freshContainer.offsetHeight;
    
    // ⚠️ CRITICAL FOR FLOATS: Ensure float layout is calculated
    // Floats need proper layout calculation to get accurate heights
    freshContainer.style.display = 'block';
    void freshContainer.scrollHeight; // Force complete layout calculation including floats
    freshContainer.style.display = '';
    void freshContainer.offsetHeight; // Second force to ensure float layout stability
    
    // 🔍 DEBUG: Final container state after layout forces
    /*console.log(`🔬 AFTER LAYOUT FORCES [${debugId}]:`, {
      containerHeight: freshContainer.offsetHeight,
      containerScrollHeight: freshContainer.scrollHeight,
      childrenCount: freshContainer.children.length,
      firstChildTag: freshContainer.firstElementChild?.tagName,
      lastChildTag: freshContainer.lastElementChild?.tagName
    });*/
    
    const elements = Array.from(freshContainer.children);

    // 🔍 DEBUG: Analyze element types and distribution
    const elementTypes = elements.reduce((acc, el) => {
      const tag = el.tagName;
      acc[tag] = (acc[tag] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    /*console.log(`🔬 ELEMENT ANALYSIS [${debugId}]:`, {
      totalElements: elements.length,
      elementTypes,
      firstElementHTML: elements[0]?.outerHTML?.substring(0, 100) + '...',
      lastElementHTML: elements[elements.length - 1]?.outerHTML?.substring(0, 100) + '...'
    });*/

    let currentPageContent = '';
    let currentPageHeight = 0;
    const pageHeights: number[] = []; // Track height per page for debugging
    const elementHeights: Array<{tag: string, height: number, isImage: boolean}> = []; // Track individual element heights

    //console.log(`🔬 ELEMENT PROCESSING START [${debugId}]: ${elements.length} elements`);

    for (const element of elements) {
      const elementClone = element.cloneNode(true) as HTMLElement;

      // Apply exact paragraph styling to match display
      if (elementClone.tagName === 'P') {
        elementClone.style.margin = '0'; // Match display styling - no margins for Google Docs spacing
        elementClone.style.lineHeight = '1.6';

        // Preserve cursor target markers for proper cursor positioning after pagination
        const originalElement = element as HTMLElement;
        if (originalElement.hasAttribute('data-cursor-target')) {
          elementClone.setAttribute('data-cursor-target', originalElement.getAttribute('data-cursor-target') || '');
        }
      }

      // Special handling for images - they are block elements
      let elementHeight = 0;
      const isImageEl = isImageElement(element as HTMLElement);
      const containsImages = (element as HTMLElement).querySelectorAll('img').length > 0;
      
      if (isImageEl) {
        elementHeight = getImageElementHeight(element as HTMLElement);
        
        // 🔍 DEBUG: Image element details
        const img = (element as HTMLElement).querySelector('img');
        /*console.log(`🔬 IMAGE ELEMENT [${debugId}]:`, {
          tag: element.tagName,
          height: elementHeight,
          imgSrc: img?.src?.substring(0, 50) + '...',
          imgComplete: img?.complete,
          imgNaturalWidth: img?.naturalWidth,
          imgNaturalHeight: img?.naturalHeight,
          imgWidth: img?.width,
          imgHeight: img?.height
        });*/
      } else if (containsImages) {
        // CRITICAL FIX: Handle paragraphs that contain images
        // These need special measurement that includes image heights
        
        const images = (element as HTMLElement).querySelectorAll('img');
        let allImagesLoaded = true;
        
        // Check if all images in this paragraph are loaded
        images.forEach(img => {
          const imageEl = img as HTMLImageElement;
          if (!imageEl.complete || imageEl.naturalHeight === 0) {
            allImagesLoaded = false;
          }
        });
        
        // 🔍 DEBUG: Paragraph with images details  
        /*console.log(`🔬 PARAGRAPH WITH IMAGES [${debugId}]:`, {
          tag: element.tagName,
          imageCount: images.length,
          allImagesLoaded,
          textLength: (element as HTMLElement).innerText?.length || 0,
          innerHTML: element.innerHTML.substring(0, 100) + '...'
        });*/
        
        if (!allImagesLoaded) {
          // Images not fully loaded in measurement container - try to measure the whole paragraph from editor
          //console.log(`⚠️ IMAGES NOT LOADED IN MEASUREMENT [${debugId}]: Getting actual dimensions from editor for ${images.length} images`);
          
          // Find the corresponding paragraph in the editor and use its actual height
          if (editorRootRef.current) {
            const paragraphs = editorRootRef.current.querySelectorAll('p');
            let foundMatchingParagraph = false;
            
            // Try to find this exact paragraph by matching image IDs
            for (const editorPara of Array.from(paragraphs)) {
              const editorImages = editorPara.querySelectorAll('img');
              if (editorImages.length === images.length && editorImages.length > 0) {
                // Check if image IDs match
                const measurementImageId = images[0].getAttribute('data-image-id');
                const editorImageId = editorImages[0].getAttribute('data-image-id');
                
                if (measurementImageId === editorImageId) {
                  // Found matching paragraph - use its actual rendered height
                  elementHeight = editorPara.offsetHeight || editorPara.clientHeight;
                  foundMatchingParagraph = true;
                  //console.log(`📏 Using matching editor paragraph height: ${elementHeight}px`);
                  break;
                }
              }
            }
            
            if (!foundMatchingParagraph) {
              // Fallback: Use reasonable default based on standard image size
              // Inline images don't stack, so use height of tallest image + small padding
              elementHeight = 176 + 30; // Standard image height + padding for text
              //console.log(`� Using fallback height for paragraph with unloaded images: ${elementHeight}px`);
            }
          } else {
            // No editor reference - use standard fallback
            elementHeight = 176 + 30;
          }
        } else {
          // Measure the element with loaded images
          const beforeMeasure = performance.now();
          const tempContainer = document.createElement('div');
          tempContainer.style.cssText = freshContainer.style.cssText;
          tempContainer.appendChild(elementClone);
          
          if (safeAppendChild(document.body, tempContainer)) {
            // Force layout and measure
            void tempContainer.offsetHeight;
            elementHeight = tempContainer.scrollHeight;
            
            // Remove temp container
            safeRemoveChild(document.body, tempContainer);
          } else {
            // Fallback if DOM operations are blocked during export
            elementHeight = 150;
            //console.log('⚠️ Using fallback height during export: 150px');
          }
          
          const afterMeasure = performance.now();
          
          /*console.log(`🔬 MEASURED PARAGRAPH WITH LOADED IMAGES [${debugId}]:`, {
            height: elementHeight,
            measureTime: (afterMeasure - beforeMeasure).toFixed(2) + 'ms',
            expectedMin: totalImageHeight,
            actualVsExpected: elementHeight >= totalImageHeight ? 'OK' : 'TOO_SHORT'
          });*/
        }
      } else {
        // Create temporary container to measure this element
        const beforeMeasure = performance.now();
        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = freshContainer.style.cssText;
        tempContainer.appendChild(elementClone);
        
        // Use safe append/remove functions
        if (safeAppendChild(document.body, tempContainer)) {
          // Force layout and measure
          void tempContainer.offsetHeight;
          elementHeight = tempContainer.scrollHeight;

          // Ensure empty paragraphs have minimum height (like <p><br></p>)
          if (elementClone.tagName === 'P' && elementHeight < 20) {
            elementHeight = 20; // Minimum line height for empty paragraphs
          }

          // Remove temp container safely
          safeRemoveChild(document.body, tempContainer);
        } else {
          // Fallback if DOM operations are blocked
          elementHeight = 20; // Default paragraph height
        }
        const afterMeasure = performance.now();
        
        // 🔍 DEBUG: Text element measurement details
        const hasInnerText = (element as HTMLElement).innerText?.trim().length > 0;
        const hasBR = element.innerHTML.includes('<br>');
        const measureTime = afterMeasure - beforeMeasure;
        
        // Only log interesting elements (skip routine empty paragraphs)
        if (elementHeight > 50 || measureTime > 1 || hasInnerText) {
          /*console.log(`� TEXT ELEMENT [${debugId}]:`, {
            tag: element.tagName,
            height: elementHeight,
            measureTime: measureTime.toFixed(2) + 'ms',
            hasInnerText,
            hasBR,
            textLength: (element as HTMLElement).innerText?.length || 0,
            innerHTML: element.innerHTML.substring(0, 80) + '...'
          });*/
        }
      }

      // Track element height for analysis
      elementHeights.push({
        tag: element.tagName,
        height: elementHeight,
        isImage: isImageEl || containsImages // Count both direct images and paragraphs with images
      });

      // 🔍 DEBUG: Log problematic elements
      if (elementHeight > PAGE_CONTENT_HEIGHT) {
        //console.log(`⚠️ OVERSIZED ELEMENT [${debugId}]: ${element.tagName} height ${elementHeight}px > ${PAGE_CONTENT_HEIGHT}px limit`);
      }

      // Check if element fits on current page - IMPROVED DEBUGGING
      const currentHeight = currentPageHeight;
      const elemHeight = elementHeight;
      const willOverflow = currentHeight + elemHeight > PAGE_CONTENT_HEIGHT;
      
      //console.log(`🔍 PAGINATION CHECK [${debugId}]: ${element.tagName} - Current: ${currentHeight}px + Element: ${elemHeight}px = ${currentHeight + elemHeight}px ${willOverflow ? '> OVERFLOW!' : '<= OK'} (limit: ${PAGE_CONTENT_HEIGHT}px)`);
      
      if (currentPageHeight + elementHeight > PAGE_CONTENT_HEIGHT) {
        // Start new page if current has content
        if (currentPageContent.trim()) {
          pages.push(currentPageContent);
          pageHeights.push(currentPageHeight);
          //console.log(`� PAGE COMPLETE [${debugId}]: Page ${pages.length} = ${currentPageHeight}px (${currentPageContent.length} chars)`);
          currentPageContent = '';
          currentPageHeight = 0;
        }

        // If element still doesn't fit on empty page, try to split it (but not images)
        if (elementHeight > PAGE_CONTENT_HEIGHT && element.tagName === 'P') {
          // CRITICAL FIX: Don't split paragraphs that contain images - they lose content
          if (containsImages) {
            //console.log(`🔬 OVERSIZED PARAGRAPH WITH IMAGES [${debugId}]: ${elementHeight}px > ${PAGE_CONTENT_HEIGHT}px - placing on new page without splitting to preserve images`);
            // Place the oversized image paragraph on its own page without splitting
            // This preserves all images and content
            currentPageContent += element.outerHTML;
            currentPageHeight += elementHeight;
          } else {
            //console.log(`🔬 SPLITTING OVERSIZED TEXT PARAGRAPH [${debugId}]: ${elementHeight}px > ${PAGE_CONTENT_HEIGHT}px`);
            const splitResult = splitParagraphByLines(element as HTMLParagraphElement);

            for (const part of splitResult) {
              if (currentPageHeight + part.height > PAGE_CONTENT_HEIGHT) {
                if (currentPageContent.trim()) {
                  pages.push(currentPageContent);
                  pageHeights.push(currentPageHeight);
                  //console.log(`🔬 SPLIT PAGE [${debugId}]: Page ${pages.length} = ${currentPageHeight}px`);
                  currentPageContent = '';
                  currentPageHeight = 0;
                }
              }

              currentPageContent += part.html;
              currentPageHeight += part.height;
            }
          }
        } else {
          // Add element to current (new) page
          // Images that are too large will just be placed on their own page
          currentPageContent += element.outerHTML;
          currentPageHeight += elementHeight;
        }
      } else {
        // Add element to current page
        currentPageContent += element.outerHTML;
        currentPageHeight += elementHeight;
      }
    }

    // Add final page if it has content
    if (currentPageContent.trim()) {
      pages.push(currentPageContent);
      pageHeights.push(currentPageHeight);
      //console.log(`� FINAL PAGE [${debugId}]: Page ${pages.length} = ${currentPageHeight}px (${currentPageContent.length} chars)`);
    }

    // Ensure at least one page exists (never delete the first page, even if empty)
    if (pages.length === 0) {
      pages.push('<p><br></p>');
      pageHeights.push(20);
      //console.log(`� EMPTY DOCUMENT [${debugId}]: Created single blank page`);
    }
    
    // CRITICAL: If we only have one page and it's empty or nearly empty, keep it as a blank page
    // This prevents the first page from disappearing when user presses Delete/Backspace
    if (pages.length === 1) {
      const firstPageContent = pages[0].trim();
      // Check if empty, or only contains empty tags like <p></p>, <br>, etc.
      const isEmpty = firstPageContent === '' || 
                      firstPageContent === '<p></p>' || 
                      firstPageContent === '<br>' ||
                      firstPageContent === '<p><br></p>' ||
                      firstPageContent === '<div></div>' ||
                      firstPageContent === '<div><br></div>';
      
      if (isEmpty || firstPageContent.length === 0) {
        pages[0] = '<p><br></p>';
        pageHeights[0] = 20;
        //console.log('🛡️ Protected first page - ensuring it stays as blank page');
      }
    }

    // 🔍 COMPREHENSIVE DEBUG SUMMARY
    const totalContentHeight = pageHeights.reduce((sum, h) => sum + h, 0);
    const avgElementHeight = elementHeights.length > 0 ? elementHeights.reduce((sum, e) => sum + e.height, 0) / elementHeights.length : 0;
    const imageElements = elementHeights.filter(e => e.isImage);
    const textElements = elementHeights.filter(e => !e.isImage);
    
    
    return pages;
  }, [splitParagraphByLines, ensureMeasurementContainerReady, safeAppendChild, safeRemoveChild]);

  /**
   * Memoized pagination - smart recalculation with image loading awareness
   */
  const pagesHTML = useMemo(() => {
    // Always try to paginate properly, but with safeguards
    if (!measureContainerRef.current) {
      //console.log('⚠️ PAGINATION BLOCKED: No measurement container');
      return [editorHTML || '<p><br></p>'];
    }

    //console.log(`🧮 PAGINATION TRIGGER: Throttled content ${editorHTML.length} chars`);
    
    const result = computePagination(editorHTML);
    
    // 🔍 CRITICAL FIX: If first calculation shows images not loaded, schedule a retry
    // This fixes the race condition where images load after first pagination
    const hasImages = editorHTML.includes('<img');
    if (hasImages && result.length > 0) {
      // Check if this looks like an image-loading-issue calculation by examining content
      const avgPageSize = editorHTML.length / result.length;
      
      // If we have many pages with small average content, images likely weren't loaded
      if (result.length > 7 && avgPageSize < 6000) {
        //console.log(`🔧 DETECTED LIKELY IMAGE LOADING ISSUE: ${result.length} pages with avg ${avgPageSize.toFixed(0)} chars/page - scheduling retry`);
        
        // Schedule a retry after a short delay to let images load
        setTimeout(() => {
          // Force a recalculation by triggering a tiny content change
          if (editorRootRef.current) {
            const currentHTML = extractContentFromPages(editorRootRef.current);
            // Add a space and remove it to trigger recalculation without actual content change
            setEditorHTML(currentHTML + ' ');
            setTimeout(() => setEditorHTML(currentHTML), 10);
          }
        }, 500); // 500ms delay for images to load
      }
    }
    
    return result;
  }, [editorHTML, computePagination]);

  /**
   * Track page count changes for automatic page management
   */
  const [previousPageCount, setPreviousPageCount] = useState(1);

  useEffect(() => {
    if (pagesHTML.length !== previousPageCount) {
      setPreviousPageCount(pagesHTML.length);
    }
  }, [pagesHTML.length, previousPageCount]);

  // Step 2: Extract overlay positions after pagination updates
  const extractOverlayPositions = useCallback(() => {
    if (!onOverlayPositionsChange) {
      return;
    }
    
    // 🎯 PREVENT overlay position extraction during PDF export (except for one final update)
    if (isExportingRef.current && !allowOneMoreOverlayUpdate.current) {
      //console.log(`⏸️ Skipping overlay position extraction - export in progress`);
      return;
    }
    
    // Reset the flag after the final update
    if (allowOneMoreOverlayUpdate.current) {
      allowOneMoreOverlayUpdate.current = false;
      //console.log(`✅ Final overlay position extraction before export`);
    }
    
    if (!editorRootRef.current) {
      return;
    }

    const containers = editorRootRef.current.querySelectorAll('.inline-image-container[data-overlay-id]');
    const positions: OverlayPositionData[] = [];

    // Find the scrollable container for container-relative positioning
    const scrollContainer = document.querySelector('[data-scroll-container]') as HTMLElement;
    if (!scrollContainer) {
      console.warn('Scroll container not found for overlay positioning');
      return;
    }

    // Check if any images are still loading or have zero dimensions
    let hasUnloadedImages = false;
    containers.forEach(container => {
      const img = container.querySelector('img');
      if (img && (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0)) {
        hasUnloadedImages = true;
      }
    });

    // If images are still loading, schedule another extraction
    if (hasUnloadedImages) {
      //console.log('🕐 Images still loading, scheduling overlay position extraction retry');
      setTimeout(() => extractOverlayPositions(), 500);
      return;
    }

    // Force layout before measuring
    void editorRootRef.current.offsetHeight;

    containers.forEach(container => {
      const overlayId = container.getAttribute('data-overlay-id');
      const metadataStr = container.getAttribute('data-metadata');
      const hasOverlay = container.getAttribute('data-has-overlay') === 'true';

      if (overlayId && metadataStr && hasOverlay) {
        try {
          const metadata = JSON.parse(metadataStr);
          
          // 🎯 FILTER: Only include overlays where visible === true
          if (metadata.visible !== true) {
            return;
          }
          
          const containerRect = container.getBoundingClientRect();
          const scrollContainerRect = scrollContainer.getBoundingClientRect();
          
          // Skip if container has zero dimensions (not yet laid out)
          if (containerRect.width === 0 || containerRect.height === 0) {
            return;
          }
          
          // Calculate position relative to the scroll container's content area
          // We need to subtract the scroll container's position and add the scroll offset
          const top = (containerRect.top - scrollContainerRect.top) + scrollContainer.scrollTop;
          const left = (containerRect.left - scrollContainerRect.left) + scrollContainer.scrollLeft;
          
          const containerRelativeRect = {
            left: left,
            top: top,
            width: containerRect.width,
            height: containerRect.height
          } as DOMRect;
          
          positions.push({
            id: overlayId,
            metadata,
            rect: containerRelativeRect
          });
        } catch (error) {
          console.error('Failed to parse metadata for overlay:', overlayId, error);
        }
      }
    });

    onOverlayPositionsChange(positions);
  }, [onOverlayPositionsChange]); // Note: Don't include isExportingRef in deps since it's a ref

  // Trigger position extraction after pagination updates
  useEffect(() => {
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping useEffect overlay extraction - export in progress`);
      return;
    }
    extractOverlayPositions();
  }, [pagesHTML, extractOverlayPositions]); // Note: Don't include isExportingRef in deps since it's a ref

  // Also trigger position updates when the editor HTML changes (images moving, content changes)
  useEffect(() => {
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping useEffect HTML change overlay extraction - export in progress`);
      return;
    }
    // Immediate overlay update without delay for fast response
    extractOverlayPositions();
  }, [editorHTML, extractOverlayPositions]); // Note: Don't include isExportingRef in deps since it's a ref

  // Real-time overlay position updates using MutationObserver
  useEffect(() => {
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping MutationObserver setup - export in progress`);
      return;
    }
    
    if (!editorRootRef.current) return;

    let updateTimeout: NodeJS.Timeout;
    
    const observer = new MutationObserver(() => {
      // Skip during export
      if (isExportingRef.current) return;
      
      // AGGRESSIVE throttling - only update overlays every 100ms to prevent spam
      if (updateTimeout) clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        //console.log('🔄 DOM mutation detected, updating overlay positions');
        // Single immediate extraction - no multiple attempts
        extractOverlayPositions();
      }, 25); // Fast enough to feel responsive, slow enough to prevent cascade
    });

    observer.observe(editorRootRef.current, {
      childList: true,
      subtree: true,
      attributes: false, // Don't watch attribute changes to reduce noise
      // attributeFilter: ['style', 'class'] // Watch for style/position changes
    });

    return () => {
      observer.disconnect();
      if (updateTimeout) clearTimeout(updateTimeout);
    };
  }, [extractOverlayPositions]); // Note: Don't include isExportingRef in deps since it's a ref

  // Watch for image resize events that could cause repositioning
  useEffect(() => {
    //console.log('🔍 IMAGE RESIZE OBSERVER: Temporarily disabled to test if this causes Enter delays');
    // TEMPORARILY DISABLED: Let's see if image resize observers are causing the delays
    // if (!editorRootRef.current) return;

    // const images = editorRootRef.current.querySelectorAll('.inline-image');
    // const resizeObservers: ResizeObserver[] = [];

    // images.forEach(img => {
    //   const observer = new ResizeObserver(() => {
    //     console.log('📏 Image resized, updating overlay positions');
    //     // Single immediate update when image resizes
    //     extractOverlayPositions();
    //   });
    //   observer.observe(img);
    //   resizeObservers.push(observer);
    // });

    // return () => {
    //   resizeObservers.forEach(observer => observer.disconnect());
    // };
  }, [pagesHTML, extractOverlayPositions]); // Re-setup when content changes

  // Listen for window resize to reposition overlays
  useEffect(() => {
    if (isExportingRef.current) {
      return;
    }

    let resizeTimeout: NodeJS.Timeout;

    const handleWindowResize = () => {
      // Throttle resize events to prevent excessive updates
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        //console.log('🔄 Window resized, updating overlay positions');
        extractOverlayPositions();
      }, 300); // Wait 300ms after resize stops before updating overlays
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [extractOverlayPositions]); // Note: Don't include isExportingRef in deps since it's a ref

  // Listen for custom image events from imageUtils.ts
  useEffect(() => {
    const handleImageResize = (e: CustomEvent) => {
      //console.log('🎯 Custom imageResized event received for:', e.detail.imageId);
      
      // CRITICAL FIX: Trigger pagination recalculation when image is resized
      // This ensures the document reflattens and content redistributes across pages
      // Same behavior as when typing
      if (!editorRootRef.current) return;
      
      //console.log('📐 Image resize detected - triggering pagination recalculation');
      
      // Extract current content and update HTML to trigger pagination
      // This mirrors the behavior that happens when users type
      setTimeout(() => {
        if (editorRootRef.current) {
          const mergedHTML = extractContentFromPages(editorRootRef.current);
          setEditorHTML(mergedHTML);
          
          // CRITICAL: Wait MUCH LONGER for pagination to complete AND images to be positioned
          // The pagination recalculates, updates DOM, and images flow to new pages
          // We need to wait for ALL of that to complete before extracting overlays
          
          // Extract overlays with MUCH LONGER delays to ensure images are positioned
          setTimeout(() => {
            //console.log('📍 First overlay extraction after resize (waiting for pagination)');
            extractOverlayPositions();
          }, 500); // Wait for pagination to start
          
          setTimeout(() => {
            //console.log('📍 Second overlay extraction after resize (images should be positioned)');
            extractOverlayPositions();
          }, 800); // Wait for images to flow to new pages
          
          setTimeout(() => {
            //console.log('📍 Third overlay extraction after resize (aggressive)');
            extractOverlayPositions();
          }, 1200); // More aggressive pass
          
          setTimeout(() => {
            //console.log('📍 Final overlay extraction after resize (ensure correct positioning)');
            extractOverlayPositions();
          }, 1600); // Final pass to ensure overlays are on correct pages
        }
      }, 50);
    };

    document.addEventListener('imageResized', handleImageResize as EventListener);
    
    // 🎯 NEW: Listen for metadata visibility changes
    const handleMetadataVisibilityChanged = (e: CustomEvent) => {
      // Immediately re-extract overlay positions to show/hide the overlay
      extractOverlayPositions();
    };
    
    document.addEventListener('metadataVisibilityChanged', handleMetadataVisibilityChanged as EventListener);
    
    // 🎯 NEW: Listen for metadata scale changes
    const handleMetadataScaleChanged = (e: CustomEvent) => {
      // Immediately re-extract overlay positions to apply the new scale
      extractOverlayPositions();
    };
    
    document.addEventListener('metadataScaleChanged', handleMetadataScaleChanged as EventListener);
    
    return () => {
      document.removeEventListener('imageResized', handleImageResize as EventListener);
      document.removeEventListener('metadataVisibilityChanged', handleMetadataVisibilityChanged as EventListener);
      document.removeEventListener('metadataScaleChanged', handleMetadataScaleChanged as EventListener);
    };
  }, [extractOverlayPositions]);

  // ================== UTILITIES ==================

  /**
   * Helper function to get all text nodes in a container
   */
  const getTextNodes = useCallback((node: Node): Text[] => {
    const textNodes: Text[] = [];

    if (node.nodeType === Node.TEXT_NODE) {
      textNodes.push(node as Text);
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        textNodes.push(...getTextNodes(node.childNodes[i]));
      }
    }

    return textNodes;
  }, []);

  /**
   * Calculate visual length of content (counting BRs and Images as 1)
   * This fixes the issue where empty lines (BRs) were counted as 0, causing cursor jumps
   */
  const calculateVisualLength = useCallback((node: Node): number => {
    if (!node) return 0;

    try {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = (node.textContent || '').length;
        return Math.max(0, textLength); // Ensure non-negative
      }

      // Handle Elements and DocumentFragments (which cloneContents returns)
      if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        const element = node as Element;
        // Only check tagName for Elements
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (element.tagName === 'BR') return 1;
          if (element.tagName === 'IMG') return 1;
        }

        let length = 0;
        for (let i = 0; i < node.childNodes.length; i++) {
          const childLength = calculateVisualLength(node.childNodes[i]);
          length += Math.max(0, childLength); // Ensure non-negative contributions
        }
        return Math.max(0, length); // Ensure final result is non-negative
      }

      return 0;
    } catch (error) {
      console.warn('Error in calculateVisualLength:', error);
      return 0;
    }
  }, []);

  /**
   * Restore cursor based on visual length
   */
  const restoreCursorByVisualLength = useCallback((root: Node, targetLength: number) => {
    // Early exit for invalid inputs
    if (!root || targetLength < 0 || !window.getSelection()) {
      console.warn('Invalid parameters for cursor restoration:', { root: !!root, targetLength, hasSelection: !!window.getSelection() });
      return;
    }

    let currentLength = 0;
    let found = false;
    const range = document.createRange();
    const selection = window.getSelection();

    const walk = (node: Node): boolean => {
      if (found) return true;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = (node.textContent || '').length;
        if (currentLength + textLength >= targetLength) {
          // CRITICAL: Ensure offset is valid and non-negative
          const offset = Math.max(0, Math.min(textLength, targetLength - currentLength));
          range.setStart(node, offset);
          range.collapse(true);
          found = true;
          return true;
        }
        currentLength += textLength;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        if (element.tagName === 'BR') {
          currentLength++;
          if (currentLength === targetLength) {
            range.setStartAfter(node);
            range.collapse(true);
            found = true;
            return true;
          }
        } else if (element.tagName === 'IMG') {
          currentLength++;
          if (currentLength === targetLength) {
            range.setStartAfter(node);
            range.collapse(true);
            found = true;
            return true;
          }
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            if (walk(node.childNodes[i])) return true;
          }
        }
      }
      return false;
    };

    try {
      walk(root);

      if (found && selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch (error) {
      console.warn('Error during cursor restoration:', error);
      // Fallback: try to place cursor at the end of the root element
      try {
        if (selection && root) {
          const fallbackRange = document.createRange();
          fallbackRange.selectNodeContents(root);
          fallbackRange.collapse(false); // Move to end
          selection.removeAllRanges();
          selection.addRange(fallbackRange);
        }
      } catch (fallbackError) {
        console.warn('Fallback cursor restoration also failed:', fallbackError);
      }
    }
  }, []);

  /**
   * Update paginated editor content with debouncing to prevent cursor jumps
   * DISABLED during normal typing to prevent infinite re-renders
   */

  useEffect(() => {
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping pagination useEffect - export in progress`);
      return;
    }
    
    if (!editorRootRef.current) return;

    // Skip pagination update if we just pressed Enter or resizing image
    if (skipNextPaginationUpdate.current || isResizingRef.current) {
      skipNextPaginationUpdate.current = false;
      return;
    }

    // Handle initial load immediately with enhanced layout forcing
    if (isInitialLoad.current) {
      // Ensure we have at least one page (Google Docs behavior)
      const pagesToRender = pagesHTML.length > 0 ? pagesHTML : ['<p><br></p>'];

      // 🔍 CRITICAL: Prevent rendering broken pagination on first load (safety check)
      // With pre-loading, this should rarely trigger, but kept as final safeguard
      const contentSize = editorHTML.length;
      const pageCount = pagesToRender.length;
      const hasImages = editorHTML.includes('<img');
      
      // Safety check: if we have substantial content with images but suspiciously few pages
      if (hasImages && contentSize > 8000 && pageCount === 1) {
        //console.log(`🚨 BLOCKING SUSPICIOUS LAYOUT: ${contentSize} chars with images but only ${pageCount} page - retrying...`);
        
        // Force immediate re-pagination before rendering
        setTimeout(() => {
          if (measureContainerRef.current) {
            measureContainerRef.current.innerHTML = '';
            void measureContainerRef.current.offsetHeight;
            triggerPagination(); // This will cause another useEffect cycle with correct pages
          }
        }, 50);
        
        return; // Don't render the broken layout
      }

      // COMPACT HTML to avoid whitespace text nodes that mess up cursor calculations
      const newPaginatedHTML = pagesToRender.map((pageHTML, index) =>
        `<div class="page-container" data-page-number="${index + 1}"><div class="page-content">${pageHTML}</div></div>`
      ).join('');

      //console.log(`🎯 INITIAL LOAD: Rendering ${pagesToRender.length} page(s)`);
      
      // 🔍 DEBUG: Check if pages have reasonable content distribution
      pagesToRender.forEach((pageHTML, index) => {
        const charCount = pageHTML.length;
        const hasImages = pageHTML.includes('<img');
        //console.log(`   📄 Page ${index + 1}: ${charCount} chars${hasImages ? ' + IMAGES' : ''}`);
      });
      
      editorRootRef.current.innerHTML = newPaginatedHTML;
      
      // CRITICAL: Force layout recalculation immediately after innerHTML change
      void editorRootRef.current.offsetHeight; // Force reflow
      void editorRootRef.current.scrollTop; // Force repaint
      
      // CRITICAL: Rebind overlays after pagination destroys DOM
      rebindAllImageOverlays(editorRootRef.current);
      
      isInitialLoad.current = false;

      // Enhanced focus handling with small delay for layout stability
      setTimeout(() => {
        if (editorRootRef.current) {
          editorRootRef.current.focus();
          
          // Additional layout recalculation after focus
          setTimeout(() => {
            //console.log('🔧 Post-focus layout recalculation...');
            triggerPagination();
          }, 50);
        }
      }, 100);

      return;
    }

    // For subsequent updates, use throttling to prevent excessive updates
    const now = Date.now();
    const timeSinceLastPagination = now - lastPaginationTime.current;

    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
    }

    // If enough time has passed (or forced by triggerPagination), update almost immediately
    // Otherwise debounce to prevent typing lag
    const delay = timeSinceLastPagination > PAGINATION_THROTTLE ? 10 : 200;

    updateTimeout.current = setTimeout(() => {
      if (!editorRootRef.current) return;

      // Save cursor position before DOM update
      const selection = window.getSelection();
      let cursorVisualLength = -1;

      if (selection && selection.rangeCount > 0) {
        try {
          const range = selection.getRangeAt(0);
          const preCaretRange = range.cloneRange();
          preCaretRange.selectNodeContents(editorRootRef.current);
          preCaretRange.setEnd(range.endContainer, range.endOffset);

          // Use visual length calculation instead of toString().length
          const fragment = preCaretRange.cloneContents();
          cursorVisualLength = calculateVisualLength(fragment);
          
          // Ensure the calculated length is valid
          if (cursorVisualLength < 0 || isNaN(cursorVisualLength)) {
            console.warn('Invalid cursor visual length calculated:', cursorVisualLength);
            cursorVisualLength = -1;
          }
        } catch (error) {
          console.warn('Error calculating cursor position:', error);
          cursorVisualLength = -1;
        }
      }

      // Update DOM structure - ensure at least one page (Google Docs behavior)
      const pagesToRender = pagesHTML.length > 0 ? pagesHTML : ['<p><br></p>'];

      // COMPACT HTML to avoid whitespace text nodes
      const newPaginatedHTML = pagesToRender.map((pageHTML, index) =>
        `<div class="page-container" data-page-number="${index + 1}"><div class="page-content">${pageHTML}</div></div>`
      ).join('');

      //console.log(`� SUBSEQUENT UPDATE: ${pagesToRender.length} page(s) (triggered by ${timeSinceLastPagination > PAGINATION_THROTTLE ? 'throttle timeout' : 'user input'})`);

      editorRootRef.current.innerHTML = newPaginatedHTML;

      // ENHANCED: Force layout recalculation immediately (same as typing does)
      void editorRootRef.current.offsetHeight; // Force reflow
      void editorRootRef.current.scrollTop; // Force repaint
      
      // Additional CSS containment trigger
      const style = window.getComputedStyle(editorRootRef.current);
      void style.getPropertyValue('contain'); // Force style recalculation

      // CRITICAL: Rebind overlays after pagination destroys DOM
      rebindAllImageOverlays(editorRootRef.current);
      
      // Initialize context menus for any images that were just added
      initializeAllImageContextMenus(editorRootRef.current);

      // Update pagination timestamp
      lastPaginationTime.current = Date.now();

      // CRITICAL: Update overlay positions after DOM changes
      setTimeout(() => {
        extractOverlayPositions();
      }, 50);

      // Restore cursor position if we had one
      if (cursorVisualLength !== -1 && cursorVisualLength >= 0 && editorRootRef.current) {
        //console.log(`🔄 useEffect pagination: Restoring cursor to position ${cursorVisualLength} (isEnterKeyProcessing: ${isEnterKeyProcessing.current})`);
        restoreCursorByVisualLength(editorRootRef.current, cursorVisualLength);
        
        // Verify restoration
        try {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0 && editorRootRef.current) {
            const r = sel.getRangeAt(0);
            const pre = r.cloneRange();
            pre.selectNodeContents(editorRootRef.current);
            pre.setEnd(r.endContainer, r.endOffset);
            const actualPos = calculateVisualLength(pre.cloneContents());
            //console.log(`   ✓ useEffect: Cursor at position ${actualPos}`);
          }
        } catch (e) {}
      }
    }, delay);

    return () => {
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
    };
  }, [pagesHTML, getTextNodes]); // Note: Don't include isExportingRef in deps since it's a ref

  // ================== INITIALIZATION ==================

  /**
   * Initialize paginated editor content on first load
   * Ensures we always have at least one blank page visible (like Google Docs)
   */
  useEffect(() => {
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping initialization useEffect - export in progress`);
      return;
    }
    
    if (editorRootRef.current && !isLoading) {
      // console.log('🔄 Initializing paginated editor content');

      // Ensure we have at least one page
      const pagesToRender = pagesHTML.length > 0 ? pagesHTML : ['<p><br></p>'];

      // COMPACT HTML
      const initialPaginatedHTML = pagesToRender.map((pageHTML, index) =>
        `<div class="page-container" data-page-number="${index + 1}"><div class="page-content">${pageHTML}</div></div>`
      ).join('');

      editorRootRef.current.innerHTML = initialPaginatedHTML;

      // CRITICAL: Rebind overlays after pagination destroys DOM
      rebindAllImageOverlays(editorRootRef.current);

      // Initialize context menus for any existing images
      initializeAllImageContextMenus(editorRootRef.current);

      // CRITICAL: Aggressive overlay position extraction after DOM setup (same as image resize)
      setTimeout(() => {
        extractOverlayPositions();
      }, 50);
      setTimeout(() => {
        extractOverlayPositions();
      }, 150);
      setTimeout(() => {
        extractOverlayPositions();
      }, 300);
      setTimeout(() => {
        extractOverlayPositions();
      }, 500);

      // CRITICAL: Force layout recalculation to fix spacing issues on page load/refresh
      // This fixes the issue where content appears messed up until typing starts
      setTimeout(() => {
        // Ensure all images are loaded before recalculating
        const images = editorRootRef.current?.querySelectorAll('img');
        let loadedImages = 0;
        const totalImages = images?.length || 0;
        
        if (totalImages === 0) {
          // No images, safe to recalculate immediately
          forceAggressiveLayoutRecalculation();
        } else {
          // Wait for images to load
          const checkImagesAndRecalculate = () => {
            loadedImages++;
            if (loadedImages >= totalImages) {
              forceAggressiveLayoutRecalculation();
            }
          };
          
          images?.forEach(img => {
            if (img.complete) {
              checkImagesAndRecalculate();
            } else {
              img.onload = checkImagesAndRecalculate;
              img.onerror = checkImagesAndRecalculate; // Count failed loads too
            }
          });
          
          // Fallback - recalculate after timeout even if images don't load
          setTimeout(() => {
            forceAggressiveLayoutRecalculation();
          }, 1000);
        }
      }, 100);

      // Focus the editor after a brief delay
      setTimeout(() => {
        if (editorRootRef.current) {
          editorRootRef.current.focus();

          // Place cursor at the start of the first page
          const firstPageContent = editorRootRef.current.querySelector('.page-content');
          if (firstPageContent) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(firstPageContent, 0);
            range.collapse(true);
            if (selection) {
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]); // Note: Don't include isExportingRef in deps since it's a ref

  // ================== FIREBASE INTEGRATION ==================

  /**
   * Update save status and notify parent component
   */
  const updateSaveStatus = useCallback((status: 'saved' | 'saving' | 'unsaved') => {
    // saveStatus state removed but function kept for compatibility
    onSaveStatusChange?.(status);
  }, [onSaveStatusChange]);

  /**
   * Save document to Firebase
   */
  const saveDocument = useCallback(async (htmlContent: string) => {
    if (!reportId || !user?.uid || isSharedReport || readOnly) return;

    try {
      updateSaveStatus('saving');

      // Convert to pages for Firebase storage format
      const pages = computePagination(htmlContent);

      const documentContent = {
        reportId,
        userId: user.uid,
        lastModified: new Date(),
        pages: pages.map((pageHTML, index) => ({
          id: `page-${index + 1}`,
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT,
          pageNumber: index + 1,
          textBlocks: [{
            id: 'text-1',
            content: pageHTML,
            x: PAGE_MARGIN,
            y: PAGE_MARGIN,
            width: PAGE_WIDTH - (PAGE_MARGIN * 2),
            height: PAGE_CONTENT_HEIGHT,
            fontSize: 12,
            fontFamily: '"Times New Roman", serif',
            color: '#333333',
            zIndex: 1
          }],
          images: [],
          backgroundColor: '#ffffff'
        }))
      };

      await FirebaseService.saveDocumentContent(documentContent);
      // console.log('✅ Document successfully saved to Firebase');
      updateSaveStatus('saved');
    } catch (error) {
      console.error('❌ Error saving document:', error);
      updateSaveStatus('unsaved');
    }
  }, [reportId, user?.uid, isSharedReport, readOnly, computePagination, updateSaveStatus]);

  // ================== EDITOR EVENT HANDLERS ==================

  /**
   * Extract merged HTML content from all page containers
   */
  const extractContentFromPages = useCallback((editorElement: HTMLDivElement): string => {
    const pageContainers = editorElement.querySelectorAll('.page-container .page-content');
    const contentParts: string[] = [];

    pageContainers.forEach(pageContent => {
      const html = (pageContent as HTMLElement).innerHTML;
      if (html.trim()) {
        contentParts.push(html);
      }
    });

    const mergedContent = contentParts.join('');
    return mergedContent || '<p><br></p>';
  }, []);

  // Manual pagination trigger for specific cases
  const triggerPagination = useCallback(() => {
    //console.log(`🔍 triggerPagination() CALLED at ${Date.now()}`);
    
    // 🎯 PREVENT pagination updates during PDF export
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping pagination - export in progress`);
      return;
    }
    
    if (editorRootRef.current) {
      const mergedHTML = extractContentFromPages(editorRootRef.current);
      // console.error('🔧 Manual pagination triggered');

      // Force immediate pagination
      lastPaginationTime.current = 0; // Reset throttle
      setEditorHTML(mergedHTML);
    }
  }, [extractContentFromPages]); // Note: Don't include isExportingRef in deps since it's a ref

  // Shared aggressive layout recalculation function - same behavior as when typing fixes the layout
  const forceAggressiveLayoutRecalculation = useCallback(() => {
    // 🎯 PREVENT aggressive pagination during PDF export
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping aggressive layout recalculation - export in progress`);
      return;
    }
    
    // Multiple aggressive pagination updates to ensure layout is completely fixed
    // This mimics what happens when typing that fixes the document layout
    triggerPagination();
    setTimeout(() => triggerPagination(), 50);
    setTimeout(() => triggerPagination(), 150);
    setTimeout(() => triggerPagination(), 300);
    setTimeout(() => triggerPagination(), 500);
    setTimeout(() => triggerPagination(), 800);
    setTimeout(() => triggerPagination(), 1200);
  }, [triggerPagination]); // Note: Don't include isExportingRef in deps since it's a ref

  /**
   * Smart input handler - throttled pagination updates
   */
  const handleEditorInput = useCallback((event: React.FormEvent<HTMLDivElement>) => {
    if (readOnly) return;

    //console.log(`⌨️ handleEditorInput fired (isEnterKeyProcessing: ${isEnterKeyProcessing.current})`);

    // 🎯 PREVENT input handling during PDF export
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping input handler - export in progress`);
      return;
    }

    // 🔧 CRITICAL: Skip input handling during Enter key processing to prevent race conditions
    if (isEnterKeyProcessing.current) {
      return;
    }

    const target = event.target as HTMLDivElement;

    // Clear any selected images when user starts typing
    hideResizeHandles();

    // Update save status immediately for user feedback
    updateSaveStatus('unsaved');

    // Check cursor position for instant pagination when near bottom of page
    const selection = window.getSelection();
    // console.error('⌨️ handleEditorInput fired', {
    //   rangeCount: selection?.rangeCount,
    //   lastPaginationTime: lastPaginationTime.current,
    //   now: Date.now()
    // });

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Find the current page the cursor is in using DOM traversal (more robust than coordinates)
      let currentPage: Element | null = null;
      const startNode = range.startContainer;
      const element = startNode.nodeType === Node.ELEMENT_NODE ? (startNode as Element) : startNode.parentElement;
      currentPage = element?.closest('.page-container') || null;

      // If cursor is within 80px of page bottom OR we've overflowed, trigger instant pagination
      if (currentPage) {
        const pageRect = currentPage.getBoundingClientRect();
        // Check if we are near bottom OR if we have already overflowed past the bottom
        // We use PAGE_HEIGHT (842px) to check against the theoretical bottom, in case the container grew
        const theoreticalBottom = pageRect.top + PAGE_HEIGHT;

        // console.log(`🔍 Cursor Check: Page ${currentPage.getAttribute('data-page-number')}`);
        // console.log(`   - Cursor Bottom: ${rect.bottom.toFixed(0)}`);
        // console.log(`   - Page Rect Bottom: ${pageRect.bottom.toFixed(0)}`);
        // console.log(`   - Theoretical Bottom: ${theoreticalBottom.toFixed(0)}`);

        if (rect.bottom > pageRect.bottom - 80 || rect.bottom > theoreticalBottom - 80) {
          //console.log('⚡ INSTANT PAGINATION TRIGGERED - Near bottom or Overflow');
          // Trigger immediate pagination
          const mergedHTML = extractContentFromPages(target);
          setEditorHTML(mergedHTML);
          return; // Skip throttled logic since we handled immediately
        }
      } else {
        //console.log('⚠️ Cursor not found within any .page-container - forcing immediate pagination');
        // If we can't find the page, we might be in a weird state or overflowed significantly
        // Trigger immediate pagination to fix layout
        const mergedHTML = extractContentFromPages(target);
        setEditorHTML(mergedHTML);
        return; // Skip throttled logic since we handled immediately
      }
    }

    // Check if we need pagination (content might be overflowing)
    const now = Date.now();
    const timeSinceLastPagination = now - lastPaginationTime.current;

    // If it's been a while since last pagination, trigger a smart update
    if (timeSinceLastPagination > PAGINATION_THROTTLE) {
      // console.error('⏱️ Throttle passed, updating editorHTML', { timeSinceLastPagination });
      const mergedHTML = extractContentFromPages(target);

      // Always update if throttle time passed
      setEditorHTML(mergedHTML);
    }
  

    // Set up auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const mergedHTML = extractContentFromPages(target);
      setEditorHTML(mergedHTML);
      saveDocument(mergedHTML);
    }, 2000);

  }, [readOnly, saveDocument, extractContentFromPages, updateSaveStatus, PAGINATION_THROTTLE]); // Note: Don't include isExportingRef in deps since it's a ref



  /**
   * Simplified keyboard handler - no cursor manipulation
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;

    // 🎯 PREVENT keyboard actions during PDF export
    if (isExportingRef.current) {
      event.preventDefault();
      return;
    }

    // Handle Delete/Backspace for selected images
    if (event.key === 'Delete' || event.key === 'Backspace') {
      const selectedImage = document.querySelector('.inline-image.selected') as HTMLImageElement;
      if (selectedImage && selectedImage.classList.contains('selected')) {
        // Only handle image deletion if image is actually selected AND visible
        const imageContainer = selectedImage.closest('.inline-image-container');
        if (imageContainer) {
          event.preventDefault();

          // Remove the entire image container from DOM
          imageContainer.remove();

          // Hide resize handles
          hideResizeHandles();

          // Update editor content
          if (editorRootRef.current) {
            const mergedHTML = extractContentFromPages(editorRootRef.current);
            setEditorHTML(mergedHTML);
            updateSaveStatus('unsaved');

            // Set up auto-save
            if (saveTimeoutRef.current) {
              clearTimeout(saveTimeoutRef.current);
            }
            saveTimeoutRef.current = setTimeout(() => {
              saveDocument(mergedHTML);
            }, 2000);
          }

          return; // Exit early for image deletion
        }
      }
      
      // CRITICAL: Prevent deletion of the first page when it's the only page
      // Allow content to be deleted until empty, but then block further deletion
      if (editorRootRef.current) {
        const pages = editorRootRef.current.querySelectorAll('.page-container');
        const selection = window.getSelection();
        
        // Check if we only have one page
        if (pages.length === 1 && selection && selection.rangeCount > 0) {
          const firstPage = pages[0];
          const pageContent = firstPage.querySelector('.page-content');
          
          if (pageContent) {
            // Get the HTML content to check structure, not just text
            const htmlContent = pageContent.innerHTML || '';
            const textContent = pageContent.textContent || '';
            const trimmedText = textContent.trim();
            
            // Check if page is truly empty (no text and minimal HTML)
            // Allow deletion if there are multiple paragraphs (returns) or actual content
            const paragraphs = pageContent.querySelectorAll('p');
            const hasMulitpleParagraphs = paragraphs.length > 1;
            const hasOnlyEmptyParagraph = paragraphs.length === 1 && 
                                          (paragraphs[0].innerHTML === '<br>' || 
                                           paragraphs[0].innerHTML === '' ||
                                           paragraphs[0].textContent?.trim() === '');
            
            // Only prevent deletion if:
            // 1. There's no text content AND
            // 2. There's only a single empty paragraph (no multiple returns to delete)
            if (trimmedText.length === 0 && hasOnlyEmptyParagraph && !hasMulitpleParagraphs) {
              event.preventDefault();
              //console.log('🛑 Prevented deletion - first page is already empty');
              return;
            }
          }
        }
      }
      
      // If no selected image, let the backspace/delete work normally for text
    }

    // Format shortcuts (Cmd/Ctrl + B/I/U)
    if (event.metaKey || event.ctrlKey) {
      switch (event.key.toLowerCase()) {
        case 'b':
          event.preventDefault();
          document.execCommand('bold');
          break;
        case 'i':
          event.preventDefault();
          document.execCommand('italic');
          break;
        case 'u':
          event.preventDefault();
          document.execCommand('underline');
          break;
        case 'p':
          // Cmd/Ctrl + P to manually trigger pagination
          event.preventDefault();
          triggerPagination();
          break;
      }
    }

    // Handle Enter key with Google Docs-style behavior
    if (event.key === 'Enter') {
      const enterTimestamp = Date.now();
      //console.log(`🔍 ENTER KEY PRESSED [${enterTimestamp}]: Starting Enter key handling`);
      
      event.preventDefault();

      // � CRITICAL: Block rapid Enter presses - wait for current Enter to complete
      if (isEnterKeyProcessing.current) {
        //console.log(`⚠️ ENTER BLOCKED [${enterTimestamp}]: Previous Enter still processing`);
        return;
      }

      // �🔧 CRITICAL: Set Enter key processing flag to prevent input handler interference
      isEnterKeyProcessing.current = true;
      
      // Clear any existing Enter key timeout
      if (enterKeyTimeoutRef.current) {
        clearTimeout(enterKeyTimeoutRef.current);
      }

      // Get current selection
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        isEnterKeyProcessing.current = false;
        return;
      }

      const range = selection.getRangeAt(0);

      // Find the current paragraph element
      let currentParagraph: Node | null = range.startContainer;

      // Walk up the DOM tree to find the containing paragraph
      while (currentParagraph && (currentParagraph.nodeType !== Node.ELEMENT_NODE ||
        (currentParagraph as Element).tagName !== 'P')) {
        // Stop if we hit the editor root
        if (currentParagraph === editorRootRef.current) break;

        const parent: Node | null = currentParagraph.parentNode;
        if (!parent) break;
        currentParagraph = parent;
      }

      // Ensure we found a valid paragraph element
      if (!currentParagraph || currentParagraph.nodeType !== Node.ELEMENT_NODE || (currentParagraph as Element).tagName !== 'P') {
        // Fallback: Insert simple break if not in a paragraph
        const br = document.createElement('br');
        range.deleteContents();
        range.insertNode(br);
        range.setStartAfter(br);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        isEnterKeyProcessing.current = false;
        return;
      }

      // 🎯 CRITICAL FIX: Check if current paragraph has floated images
      // If so, use <br> instead of creating new paragraph to allow text wrapping to continue
      // BUT only if we're actually typing text beside the image (not at start/end of paragraph with only images)
      const currentParaElement = currentParagraph as HTMLElement;
      const hasFloatedImage = currentParaElement.querySelector('.inline-image-container.image-float-left, .inline-image-container.image-float-right');
      
      if (hasFloatedImage) {
        // Check if there's actual text content in the paragraph (not just images)
        const paragraphText = currentParaElement.textContent?.trim() || '';
        const hasTextContent = paragraphText.length > 0;
        
        // Only use <br> behavior if there's text content (user is actually typing beside image)
        // If paragraph only has images, use normal new paragraph behavior
        if (hasTextContent) {
          // Use <br> for line break instead of new paragraph
          // This allows text to continue wrapping beside the floated image
          const br = document.createElement('br');
          range.deleteContents();
          range.insertNode(br);
          
          // Position cursor after the <br>
          range.setStartAfter(br);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Trigger input handler to update content
          if (editorRootRef.current) {
            const inputEvent = new Event('input', { bubbles: true });
            editorRootRef.current.dispatchEvent(inputEvent);
          }
          
          isEnterKeyProcessing.current = false;
          return;
        } else {
          // Fall through to normal paragraph creation
        }
      }

      // Simple behavior: Move everything after cursor to new line (like Google Docs)
      // Create a range that captures everything from cursor position to end of paragraph
      const adjustedRange = range.cloneRange();
      
      // Find the last node in the paragraph
      const paragraphElement = currentParagraph as Element;
      const walker = document.createTreeWalker(
        paragraphElement,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        null
      );
      
      let lastNode: Node = paragraphElement;
      let currentNode;
      while (currentNode = walker.nextNode()) {
        lastNode = currentNode;
      }
      
      // Set the end of the range to the very end of the paragraph content
      if (lastNode.nodeType === Node.TEXT_NODE) {
        adjustedRange.setEnd(lastNode, lastNode.textContent?.length || 0);
      } else {
        adjustedRange.setEndAfter(lastNode);
      }

      // Create new paragraph
      const newParagraph = document.createElement('p');
      newParagraph.style.margin = '0';
      newParagraph.style.lineHeight = '1.6';

      // CRITICAL: Ensure range doesn't split image containers
      const finalRange = adjustRangeForImageContainers(adjustedRange);
      
      // Use extractContents() to properly handle mixed content (text + images)
      const extractedContent = finalRange.extractContents();

      // If there's content after the cursor, put it in the new paragraph
      if (extractedContent.childNodes.length > 0) {
        // 🔧 IMPORTANT: Remove formatting tags like <strong>, <em>, <u> to prevent inheriting bold/italic/underline
        // This ensures when you press Return on a bold line, the new line is NOT bold
        const unwrapFormattingElements = (node: Node): void => {
          const nodesToProcess = Array.from(node.childNodes);
          nodesToProcess.forEach(child => {
            if (child.nodeType === Node.ELEMENT_NODE) {
              const element = child as Element;
              // Check if this is a formatting element (strong, em, u, b, i)
              if (['STRONG', 'EM', 'U', 'B', 'I'].includes(element.tagName)) {
                // Replace formatting element with its text content
                const textContent = element.textContent || '';
                if (textContent) {
                  const textNode = document.createTextNode(textContent);
                  element.parentNode?.replaceChild(textNode, element);
                } else {
                  element.remove();
                }
              } else {
                // Recursively process child elements
                unwrapFormattingElements(child);
              }
            }
          });
        };
        
        unwrapFormattingElements(extractedContent);
        newParagraph.appendChild(extractedContent);
      } else {
        newParagraph.innerHTML = '<br>';
      }

      // If the current paragraph is now empty, add a break
      if ((currentParagraph as Element).innerHTML.trim() === '') {
        (currentParagraph as Element).innerHTML = '<br>';
      }

      // Insert new paragraph AFTER the current paragraph
      (currentParagraph as Element).parentNode?.insertBefore(newParagraph, (currentParagraph as Element).nextSibling);

      // Position cursor at the BEGINNING of the new paragraph (Google Docs style)
      const newRange = document.createRange();
      
      // 🔧 CRITICAL FIX: Always ensure empty paragraphs have a BR tag for visual length
      if (!newParagraph.firstChild) {
        newParagraph.innerHTML = '<br>';
      }
      
      // 🔧 CRITICAL FIX: Position cursor to include the new paragraph in visual length
      const firstChild = newParagraph.firstChild;
      if (firstChild) {
        if (firstChild.nodeType === Node.TEXT_NODE) {
          // Position at start of first text node
          newRange.setStart(firstChild, 0);
        } else if (firstChild.nodeName === 'BR') {
          // 🔧 For <br>, we need the cursor AFTER it for correct visual length
          // Create a zero-width text node after the BR for cursor placement
          const textNode = document.createTextNode('');
          newParagraph.appendChild(textNode);
          newRange.setStart(textNode, 0);
        } else {
          // Position before the first element
          newRange.setStartBefore(firstChild);
        }
      }
      
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);

      // � DEBUG: Log cursor position immediately after Enter
      //console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      //console.log(`🎯 ENTER [${enterTimestamp}]: Step 1 - Cursor positioned in new paragraph`);
      const initialCursorInfo = {
        paragraph: newParagraph.textContent?.substring(0, 50),
        paragraphHTML: newParagraph.innerHTML,
        pageNumber: newParagraph.closest('.page-container')?.getAttribute('data-page-number'),
        visualLength: (() => {
          try {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && editorRootRef.current) {
              const r = sel.getRangeAt(0);
              //console.log(`      🔍 Cursor is in:`, r.startContainer.nodeName, `offset:`, r.startOffset);
              //console.log(`      🔍 Parent element:`, (r.startContainer.parentElement as HTMLElement)?.tagName);
              const pre = r.cloneRange();
              pre.selectNodeContents(editorRootRef.current);
              pre.setEnd(r.endContainer, r.endOffset);
              const frag = pre.cloneContents();
              const tempDiv = document.createElement('div');
              tempDiv.appendChild(frag);
              //console.log(`      🔍 Content up to cursor:`, tempDiv.innerHTML);
              return calculateVisualLength(pre.cloneContents());
            }
          } catch (e) { return -1; }
          return -1;
        })()
      };
      //console.log('   📍 Initial cursor position:', initialCursorInfo);

      // Update save status immediately for user feedback
      updateSaveStatus('unsaved');

      // 🚀 FIXED ASYNC SEQUENCE: Wait for DOM settling before pagination
      requestAnimationFrame(() => {
        //console.log(`🎬 ENTER [${enterTimestamp}]: Step 2 - requestAnimationFrame fired`);
        
        // Check cursor position after RAF
        const rafCursorInfo = {
          stillInNewPara: document.contains(newParagraph),
          visualLength: (() => {
            try {
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0 && editorRootRef.current) {
                const r = sel.getRangeAt(0);
                const pre = r.cloneRange();
                pre.selectNodeContents(editorRootRef.current);
                pre.setEnd(r.endContainer, r.endOffset);
                return calculateVisualLength(pre.cloneContents());
              }
            } catch (e) { return -1; }
            return -1;
          })()
        };
        //console.log('   📍 Cursor after RAF:', rafCursorInfo);
        
        enterKeyTimeoutRef.current = setTimeout(() => {
          //console.log(`⏱️ ENTER [${enterTimestamp}]: Step 3 - DOM settled, saving cursor position`);
          
          // 🔧 CRITICAL FIX: Force a layout reflow to ensure the new paragraph is fully rendered
          if (editorRootRef.current && newParagraph.parentNode) {
            void editorRootRef.current.offsetHeight; // Force reflow
            void newParagraph.offsetHeight; // Force paragraph layout
          }
          
          // 🔧 SAVE CURSOR POSITION AFTER LAYOUT REFLOW
          let savedCursorPosition = -1;
          try {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && editorRootRef.current) {
              const r = sel.getRangeAt(0);
              const pre = r.cloneRange();
              pre.selectNodeContents(editorRootRef.current);
              pre.setEnd(r.endContainer, r.endOffset);
              savedCursorPosition = calculateVisualLength(pre.cloneContents());
              //console.log(`   💾 Saved cursor position: ${savedCursorPosition}`);
              
              // 🔍 DEBUG: Show what we're measuring
              const debugFragment = pre.cloneContents();
              const tempDiv = document.createElement('div');
              tempDiv.appendChild(debugFragment);
              //console.log(`   🔍 Measuring content:`, tempDiv.innerHTML.substring(0, 200));
            }
          } catch (error) {
            console.error('   ❌ Failed to save cursor position:', error);
          }
          
          // Now DOM has settled - safe to trigger pagination
          if (editorRootRef.current) {
            //console.log(`📄 ENTER [${enterTimestamp}]: Step 4 - Triggering pagination (DOM will be destroyed)`);
            const mergedHTML = extractContentFromPages(editorRootRef.current);
            setEditorHTML(mergedHTML);
            
            // Update overlays after pagination
            setTimeout(() => {
              //console.log(`� ENTER [${enterTimestamp}]: Step 5 - Pagination complete, restoring cursor`);
              extractOverlayPositions();
              
              // 🔧 RESTORE CURSOR POSITION AFTER PAGINATION
              if (savedCursorPosition !== -1 && editorRootRef.current) {
                //console.log(`   🎯 Restoring cursor to position: ${savedCursorPosition}`);
                restoreCursorByVisualLength(editorRootRef.current, savedCursorPosition);
                
                // Verify where cursor ended up
                try {
                  const sel = window.getSelection();
                  if (sel && sel.rangeCount > 0 && editorRootRef.current) {
                    const r = sel.getRangeAt(0);
                    const pre = r.cloneRange();
                    pre.selectNodeContents(editorRootRef.current);
                    pre.setEnd(r.endContainer, r.endOffset);
                    const actualPosition = calculateVisualLength(pre.cloneContents());
                    const pageNum = r.startContainer.parentElement?.closest('.page-container')?.getAttribute('data-page-number');
                    //console.log(`   ✅ Cursor restored to position: ${actualPosition} (page ${pageNum})`);
                    
                    if (actualPosition !== savedCursorPosition) {
                      console.warn(`   ⚠️ CURSOR MISMATCH! Expected ${savedCursorPosition}, got ${actualPosition}`);
                    }
                  }
                } catch (e) {
                  console.error('   ❌ Failed to verify cursor position:', e);
                }
              } else {
                console.warn(`   ⚠️ Could not restore cursor - saved position was ${savedCursorPosition}`);
              }
              
              // Clear Enter key processing flag - sequence complete
              isEnterKeyProcessing.current = false;
              //console.log(`🏁 ENTER [${enterTimestamp}]: Sequence complete`);
              //console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
            }, 50); // Brief delay for pagination to complete
          } else {
            isEnterKeyProcessing.current = false;
          }
        }, 150); // DOM settling delay - critical for proper measurement
      });

      // Set up auto-save (separate from pagination)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        if (editorRootRef.current) {
          const mergedHTML = extractContentFromPages(editorRootRef.current);
          saveDocument(mergedHTML);
        }
      }, 2000);

      return;
    }

    // Let browser handle all other keys naturally - no custom cursor logic
  }, [readOnly, extractContentFromPages, saveDocument, updateSaveStatus, isExporting]);

  /**
   * Handle cursor navigation to check for instant pagination needs
   */
  const handleKeyUp = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;

    // Check for navigation keys that might move cursor to page bottom
    if (['ArrowDown', 'ArrowRight', 'End', 'PageDown'].includes(event.key)) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        // Find the current page the cursor is in using DOM traversal
        let currentPage: Element | null = null;
        const startNode = range.startContainer;
        const element = startNode.nodeType === Node.ELEMENT_NODE ? (startNode as Element) : startNode.parentElement;
        currentPage = element?.closest('.page-container') || null;

        // If cursor is within 80px of page bottom, trigger instant pagination
        if (currentPage) {
          const pageRect = currentPage.getBoundingClientRect();
          if (rect.bottom > pageRect.bottom - 80) {
            // Trigger immediate pagination
            setTimeout(() => triggerPagination(), 10);
          }
        }
      }
    }
  }, [readOnly, triggerPagination]);

  // ================== IMAGE DRAG & DROP HANDLERS ==================

  /**
   * Handle drag over event to show drop target
   */
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (readOnly) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';

    setIsDraggingOver(true);
  }, [readOnly]);

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Only hide if we're leaving the editor entirely
    const relatedTarget = event.relatedTarget as Node;
    if (!editorRootRef.current?.contains(relatedTarget)) {
      setIsDraggingOver(false);
    }
  }, []);

  /**
   * Create a simple entry layout with title, notes, and images
   */
  const insertFormattedEntry = useCallback((entry: any) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);

    // Clear any selection content
    range.deleteContents();

    // Add entry title as a regular paragraph
    const titleElement = document.createElement('p');
    titleElement.innerHTML = `<strong>${entry.title || entry.name || 'Entry'}</strong>`;
    titleElement.style.cssText = `
      margin: 0;
      line-height: 1.6;
    `;
    range.insertNode(titleElement);

    // Move range after title
    range.setStartAfter(titleElement);

    // Add notes if available
    if (entry.note && entry.note.trim()) {
      const notesElement = document.createElement('p');
      notesElement.textContent = entry.note;
      notesElement.style.cssText = `
        margin: 0;
        line-height: 1.6;
      `;
      range.insertNode(notesElement);
      range.setStartAfter(notesElement);
    }

    // Add images if there are any
    if (entry.images && entry.images.length > 0) {
      // Create a paragraph for images
      const imagesPara = document.createElement('p');
      imagesPara.style.cssText = `
        margin: 0;
        line-height: 1.6;
      `;

      // Add images as inline images with spaces - now with proper metadata support
      entry.images.forEach((image: any, index: number) => {
        const imageUrl = image.photoURL || image.url;
        const imageWidth = STANDARD_DRAG_IMAGE_WIDTH;

        // Check if image has metadata
        let imageMetadata: {latitude?: number; longitude?: number; address?: string; capturedAt: Date} | undefined;
        if (image.metadata) {
          const metadata = image.metadata;
          // Safely create Date object with fallback
          let capturedAtDate = new Date();
          if (metadata.capturedAt) {
            const attemptedDate = new Date(metadata.capturedAt);
            if (!isNaN(attemptedDate.getTime())) {
              capturedAtDate = attemptedDate;
            }
          }
          
          imageMetadata = {
            latitude: metadata.latitude,
            longitude: metadata.longitude,
            address: metadata.address,
            capturedAt: capturedAtDate
          };
        }

        // Create the image element
        const img = document.createElement('img');
        img.src = imageUrl;
        img.className = 'inline-image';
        img.crossOrigin = 'anonymous';
        img.contentEditable = 'false';
        img.draggable = false;

        img.style.cssText = `
          width: ${imageWidth}px;
          height: auto;
          max-width: 100%;
          display: inline-block;
          vertical-align: top;
          margin: 0 4px 0 0;
          cursor: pointer;
          border: 2px solid transparent;
          transition: border-color 0.2s ease;
        `;

        // Add data attributes for tracking
        img.setAttribute('data-original-width', imageWidth.toString());
        img.setAttribute('data-image-url', imageUrl);
        img.setAttribute('data-image-id', 'img-' + Date.now() + '-' + index);

        // Add load event handler to trigger overlay position updates when image loads
        img.addEventListener('load', () => {
          //console.log('📸 Image loaded, triggering overlay position update:', img.getAttribute('data-image-id'));
          // Small delay to ensure layout is complete
          setTimeout(() => {
            extractOverlayPositions();
          }, 50);
        });

        // Store metadata as data attributes if provided (matching insertImageAtCursor behavior)
        if (imageMetadata) {
          // 🎯 SMART DEFAULT: Only show overlay if image has valid address
          const hasValidMetadata = imageMetadata.address && 
            !imageMetadata.address.startsWith('Location:') && 
            imageMetadata.address !== 'Location Unavailable' &&
            imageMetadata.address.trim().length > 0;
          
          img.setAttribute('data-metadata-lat', imageMetadata.latitude?.toString() || '');
          img.setAttribute('data-metadata-lng', imageMetadata.longitude?.toString() || '');
          img.setAttribute('data-metadata-address', imageMetadata.address || '');
          img.setAttribute('data-metadata-captured', imageMetadata.capturedAt.toISOString());
          img.setAttribute('data-metadata-visible', hasValidMetadata ? 'true' : 'false');
        }

        // Create proper container for React portal system (matching createSimpleImageContainer)
        const container = document.createElement('span');
        // 🎯 ALL images float-left for text wrapping
        container.className = 'inline-image-container image-float-left';
        container.contentEditable = 'false'; // Critical: prevents contentEditable conflicts
        
        // Generate unique overlay ID for React portal linking
        const overlayId = img.getAttribute('data-image-id') || `overlay-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        img.setAttribute('data-image-id', overlayId);
        container.setAttribute('data-overlay-id', overlayId);
        
        // Store metadata as JSON for React portal system
        if (imageMetadata) {
          // 🎯 SMART DEFAULT: Check if metadata has valid address
          const hasValidMetadata = imageMetadata.address && 
            !imageMetadata.address.startsWith('Location:') && 
            imageMetadata.address !== 'Location Unavailable' &&
            imageMetadata.address.trim().length > 0;
          
          const serializedMetadata = {
            latitude: imageMetadata.latitude,
            longitude: imageMetadata.longitude,
            address: imageMetadata.address,
            capturedAt: imageMetadata.capturedAt.toISOString(),
            visible: hasValidMetadata // Smart default based on metadata quality
          };
          container.setAttribute('data-metadata', JSON.stringify(serializedMetadata));
          container.setAttribute('data-has-overlay', 'true');
        } else {
          container.setAttribute('data-has-overlay', 'false');
        }

        // Minimal styling - matching createSimpleImageContainer
        container.style.cssText = `
          display: inline-block;
          margin: 0 4px 0 0;
          vertical-align: top;
          max-width: 100%;
          position: relative;
        `;

        // Add image to container
        container.appendChild(img);

        // Add container to paragraph
        imagesPara.appendChild(container);

        // Add space between images (except for the last one)
        if (index < entry.images.length - 1) {
          const spaceNode = document.createTextNode(' ');
          imagesPara.appendChild(spaceNode);
        }
      });

      // CRITICAL: Add a text node after all images so user can type beside them
      const textNodeAfterImages = document.createTextNode('\u200B'); // Zero-width space
      imagesPara.appendChild(textNodeAfterImages);

      range.insertNode(imagesPara);
      
      // Position cursor in the text node after the images (not in a new paragraph)
      range.setStart(textNodeAfterImages, 0);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // No images - just position after title/notes
      // Add an empty paragraph for cursor placement
      const emptyPara = document.createElement('p');
      emptyPara.innerHTML = '<br>';
      emptyPara.style.cssText = `
        margin: 0;
        line-height: 1.6;
      `;
      range.insertNode(emptyPara);

      // Move cursor to the empty paragraph
      range.setStart(emptyPara, 0);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    return true;
  }, [extractOverlayPositions]);

  /**
   * Handle drop event to insert image or entry at the precise drop location
   */
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (readOnly) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);

    // Only handle drops that occur within the editor root or page content
    const target = event.target as HTMLElement;
    const isWithinEditor = editorRootRef.current?.contains(target);
    const isPageContent = target.closest('.page-content') || target.classList.contains('page-content');

    if (!isWithinEditor && !isPageContent) {
      //console.log('🚫 Drop ignored - not within editor area');
      return;
    }

    // Get precise drop position using mouse coordinates
    const dropX = event.clientX;
    const dropY = event.clientY;

    // console.log(`🎯 Drop position: ${dropX}, ${dropY}`);

    try {
      // Try to get JSON data first (from sidebar)
      const jsonData = event.dataTransfer.getData('application/json');
      let imageUrl = '';
      let defaultWidth = STANDARD_DRAG_IMAGE_WIDTH;
      let imageMetadata: {latitude?: number; longitude?: number; address?: string; capturedAt: Date} | undefined;

      if (jsonData) {
        const data = JSON.parse(jsonData);
        // console.log('📥 Dropped data:', data);

        // Handle entire entry drops
        if (data.type === 'entry' && data.data) {
          // console.log('📄 Inserting formatted entry:', data.data.title || data.data.name);

          // Position cursor at the exact drop location
          if (editorRootRef.current) {
            editorRootRef.current.focus();

            // Use document.caretPositionFromPoint for precise positioning
            let range: Range | null = null;

            if (document.caretPositionFromPoint) {
              const caretPos = document.caretPositionFromPoint(dropX, dropY);
              if (caretPos) {
                range = document.createRange();
                range.setStart(caretPos.offsetNode, caretPos.offset);
                range.collapse(true);
              }
            } else if (document.caretRangeFromPoint) {
              // Fallback for browsers that support caretRangeFromPoint
              range = document.caretRangeFromPoint(dropX, dropY);
            }

            // If we couldn't get precise positioning, find the nearest page content
            if (!range) {
              const pageContent = target.closest('.page-content') as HTMLElement;
              if (pageContent) {
                range = document.createRange();
                // Try to place cursor at the end of the page content
                const lastChild = pageContent.lastChild;
                if (lastChild) {
                  range.setStartAfter(lastChild);
                } else {
                  range.selectNodeContents(pageContent);
                }
                range.collapse(false);
              }
            }

            if (range) {
              const selection = window.getSelection();
              selection?.removeAllRanges();
              selection?.addRange(range);
              // console.log('📍 Cursor positioned at drop location');
            }

            // Insert the formatted entry
            const success = insertFormattedEntry(data.data);

            if (success && editorRootRef.current) {
              // console.log('✅ Entry inserted successfully');

              // Extract updated content and trigger save FIRST
              const mergedHTML = extractContentFromPages(editorRootRef.current);
              setEditorHTML(mergedHTML);
              updateSaveStatus('unsaved');

              // CRITICAL: Wait for content to be rendered, then rebind overlays
              // This ensures images are properly positioned before calculating overlay positions
              setTimeout(() => {
                if (editorRootRef.current) {
                  rebindAllImageOverlays(editorRootRef.current);
                  initializeAllImageContextMenus(editorRootRef.current);
                }
              }, 50);

              // CRITICAL: Wait for pagination to complete, then extract overlay positions
              setTimeout(() => {
                if (editorRootRef.current) {
                  //console.log('🎯 Extracting overlay positions after image insertion and pagination');
                  extractOverlayPositions();
                  // Force another update after a short delay to handle any layout shifts
                  setTimeout(() => extractOverlayPositions(), 200);
                }
              }, 150);

              // Set up auto-save
              if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
              }
              saveTimeoutRef.current = setTimeout(() => {
                saveDocument(mergedHTML);
              }, 2000);
            }
          }

          return; // Exit early for entry drops
        }

        // Handle company images from sidebar
        if (data.type === 'company-image' && data.imageUrl) {
          imageUrl = data.imageUrl;
          defaultWidth = STANDARD_DRAG_IMAGE_WIDTH; // Use consistent small size
        }
        // Handle report images from PDFEditor
        else if (data.type === 'image' && data.data) {
          imageUrl = data.data.photoURL || data.data.url;
          // Use consistent small size regardless of original dimensions
          defaultWidth = STANDARD_DRAG_IMAGE_WIDTH;
          
          // Extract metadata if available (matching ImageWithMetadata structure)
          if (data.data.metadata) {
            const metadata = data.data.metadata;
            // Safely create Date object with fallback
            let capturedAtDate = new Date();
            if (metadata.capturedAt) {
              const attemptedDate = new Date(metadata.capturedAt);
              if (!isNaN(attemptedDate.getTime())) {
                capturedAtDate = attemptedDate;
              }
            }
            
            imageMetadata = {
              latitude: metadata.latitude,
              longitude: metadata.longitude,
              address: metadata.address,
              capturedAt: capturedAtDate
            };
          }
        }
      } else {
        // Fallback to plain text URL
        const textData = event.dataTransfer.getData('text/plain');
        if (textData && (textData.startsWith('http://') || textData.startsWith('https://'))) {
          imageUrl = textData;
        }
      }

      if (!imageUrl) {
        console.warn('No valid image URL found in drop data');
        return;
      }

      // Position cursor at the exact drop location for images too
      if (editorRootRef.current) {
        editorRootRef.current.focus();

        // Use document.caretPositionFromPoint for precise positioning
        let range: Range | null = null;

        if (document.caretPositionFromPoint) {
          const caretPos = document.caretPositionFromPoint(dropX, dropY);
          if (caretPos) {
            range = document.createRange();
            range.setStart(caretPos.offsetNode, caretPos.offset);
            range.collapse(true);
          }
        } else if (document.caretRangeFromPoint) {
          // Fallback for browsers that support caretRangeFromPoint
          range = document.caretRangeFromPoint(dropX, dropY);
        }

        // If we couldn't get precise positioning, find the nearest page content
        if (!range) {
          const pageContent = target.closest('.page-content') as HTMLElement;
          if (pageContent) {
            range = document.createRange();
            // Try to place cursor at the end of the page content
            const lastChild = pageContent.lastChild;
            if (lastChild) {
              range.setStartAfter(lastChild);
            } else {
              range.selectNodeContents(pageContent);
            }
            range.collapse(false);
          }
        }

        if (range) {
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
          // console.log('📍 Cursor positioned at drop location for image');
        }
      }

      // Insert image at cursor position
      const success = insertImageAtCursor(imageUrl, defaultWidth, imageMetadata);

      if (success && editorRootRef.current) {
        // console.log('✅ Image inserted successfully');

        // Extract updated content and trigger save
        const mergedHTML = extractContentFromPages(editorRootRef.current);
        setEditorHTML(mergedHTML);
        updateSaveStatus('unsaved');

        // Trigger auto-save
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
          saveDocument(mergedHTML);
        }, 2000);
      }
    } catch (error) {
      console.error('❌ Error handling image drop:', error);
    }
  }, [readOnly, extractContentFromPages, saveDocument, updateSaveStatus]);

  /**
   * Handle click on images to show resize handles and selection feedback
   */
  const handleEditorClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    // Don't interfere with resize handle clicks
    if (target.classList.contains('resize-handle')) {
      return;
    }

    // Don't hide handles if clicking on the handles container itself
    if (target.classList.contains('image-resize-handles')) {
      return;
    }

    // 🎯 PREVENT image selection during PDF export
    if (isExportingRef.current) {
      return;
    }

    // Check if clicked on an inline image
    if (target.classList.contains('inline-image') && target.tagName === 'IMG') {
      event.preventDefault();
      event.stopPropagation();
      // console.log('🖼️ Image selected - click Delete to remove it');

      // Clear any existing selections first
      hideResizeHandles();

      // Show selection for this image
      showResizeHandles(target as HTMLImageElement);

      // 🔥 FIX: Remove cursor focus to prevent jumping back to old cursor position
      // Option 1: Clear selection completely (no visible cursor)
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
      
      // Option 2: Alternatively, place cursor after the image
      // const imageContainer = target.closest('.inline-image-container');
      // if (imageContainer && imageContainer.nextSibling) {
      //   const range = document.createRange();
      //   range.setStartAfter(imageContainer);
      //   range.collapse(true);
      //   if (selection) {
      //     selection.removeAllRanges();
      //     selection.addRange(range);
      //   }
      // }

      // Focus the editor but without restoring cursor position
      if (editorRootRef.current) {
        editorRootRef.current.focus({ preventScroll: true });
      }
    } else {
      // Only hide handles if NOT clicking on a currently selected image
      // Check if there's a selected image and we're clicking outside of it
      const selectedImage = document.querySelector('.inline-image.selected') as HTMLImageElement;
      
      if (!selectedImage || !selectedImage.contains(target)) {
        // Clicked outside the selected image, hide all handles and selections
        hideResizeHandles();
      } else {
        // Clicked on the selected image area but not on an image tag
        // Keep the handles visible
        return;
      }

      // Check cursor position after click for potential pagination
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();

          // Find the current page the cursor is in using DOM traversal
          let currentPage: Element | null = null;
          const startNode = range.startContainer;
          const element = startNode.nodeType === Node.ELEMENT_NODE ? (startNode as Element) : startNode.parentElement;
          currentPage = element?.closest('.page-container') || null;

          // If cursor is within 80px of page bottom, trigger instant pagination
          if (currentPage) {
            const pageRect = currentPage.getBoundingClientRect();
            if (rect.bottom > pageRect.bottom - 80) {
              // Trigger immediate pagination
              setTimeout(() => triggerPagination(), 10);
            }
          }
        }
      }, 10);
    }
  }, [triggerPagination, isExporting]);

  /**
   * Load document from Firebase with robust layout initialization
   * ENHANCED to prevent layout calculation race conditions
   */
  const loadDocument = useCallback(async () => {
    if (!reportId || !user?.uid) {
      setEditorHTML('<p><br></p>');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Step 1: Ensure measurement infrastructure is ready BEFORE loading content
      const measurementReady = ensureMeasurementContainerReady();
      if (!measurementReady) {
        throw new Error('Failed to initialize measurement container');
      }

      // Step 2: Load saved document content from Firebase
      const documentContent = await FirebaseService.loadDocumentContent(reportId, user.uid);

      let contentToLoad = '<p><br></p>'; // Default empty content

      if (documentContent && documentContent.pages && documentContent.pages.length > 0) {
        // Extract HTML content from the saved document pages
        let mergedHTML = '';

        for (const page of documentContent.pages) {
          // Each page should have textBlocks containing the HTML content
          if (page.textBlocks && page.textBlocks.length > 0) {
            // Combine content from all text blocks on this page
            const pageContent = page.textBlocks
              .map(textBlock => textBlock.content || '')
              .join('');
            mergedHTML += pageContent;
          }
        }

        contentToLoad = mergedHTML.trim() || '<p><br></p>';
      }

      // 🔍 DEBUG: Log what content we loaded
      //console.log(`📄 LOADED CONTENT: ${contentToLoad.length} chars, ${contentToLoad.includes('<img') ? 'HAS IMAGES' : 'NO IMAGES'}`);

      // Step 3: PRE-LOAD ALL IMAGES before setting content
      // This ensures pagination has accurate dimensions from the start
      const hasImages = contentToLoad.includes('<img');
      if (hasImages) {
        //console.log(`🖼️ Content has images - pre-loading before pagination...`);
        
        // Create a temporary hidden container to load images
        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `
          position: absolute;
          left: -9999px;
          top: -9999px;
          visibility: hidden;
          width: ${PAGE_WIDTH - (PAGE_MARGIN * 2)}px;
        `;
        document.body.appendChild(tempContainer);
        tempContainer.innerHTML = contentToLoad;
        
        // Wait for all images to load
        const images = tempContainer.querySelectorAll('img');
        //console.log(`🖼️ Found ${images.length} images to pre-load...`);
        
        if (images.length > 0) {
          const imageLoadPromises = Array.from(images).map((img) => {
            return new Promise<void>((resolve) => {
              const imageElement = img as HTMLImageElement;
              
              // If already loaded, resolve immediately
              if (imageElement.complete && imageElement.naturalHeight !== 0) {
                //console.log(`✅ Image already loaded: ${imageElement.src.substring(0, 50)}...`);
                resolve();
                return;
              }

              // Set up load handlers with timeout
              const timeout = setTimeout(() => {
                //console.log(`⚠️ Image load timeout: ${imageElement.src.substring(0, 50)}...`);
                resolve(); // Continue even if timeout
              }, 5000); // 5 second timeout per image

              imageElement.onload = () => {
                clearTimeout(timeout);
                //console.log(`✅ Image loaded: ${imageElement.src.substring(0, 50)}...`);
                resolve();
              };

              imageElement.onerror = () => {
                clearTimeout(timeout);
                //console.log(`❌ Image load error: ${imageElement.src.substring(0, 50)}...`);
                resolve(); // Continue even if error
              };
            });
          });

          // Wait for all images to load or timeout
          await Promise.all(imageLoadPromises);
          //console.log(`✅ All images pre-loaded successfully`);
        }
        
        // Clean up temp container
        document.body.removeChild(tempContainer);
      }

      // Step 4: Now set content (images are already loaded, so pagination will be accurate)
      setEditorHTML(contentToLoad);

      updateSaveStatus('saved'); // Document is saved after loading

    } catch (error) {
      console.error('❌ Error loading document:', error);
      // On error, start with empty content
      setEditorHTML('<p><br></p>');
      updateSaveStatus('saved'); // Empty document on error is considered saved
    } finally {
      // IMPORTANT: Don't set loading to false immediately
      // Let the robust layout initialization complete first
      setTimeout(async () => {
        // Final step: Ensure proper layout after everything is ready
        if (editorRootRef.current) {
          // Wait for images if content was loaded
          await waitForImagesLoaded(editorRootRef.current);
          
          // Force aggressive layout recalculation (same as typing fix)
          forceAggressiveLayoutRecalculation();
          
          // Extract overlay positions after layout is stable
          setTimeout(() => extractOverlayPositions(), 100);
          setTimeout(() => extractOverlayPositions(), 300);
          setTimeout(() => extractOverlayPositions(), 500);
        }
        
        setIsLoading(false);
      }, 100); // Small delay to let DOM settle
    }
  }, [reportId, user?.uid, updateSaveStatus, ensureMeasurementContainerReady, waitForImagesLoaded, forceAggressiveLayoutRecalculation, extractOverlayPositions]);

  // ================== IMAGE INITIALIZATION ==================

  // Image resize functionality is now handled in imageUtils.ts with showResizeHandles/hideResizeHandles

  // ================== INITIALIZATION ==================

  /**
   * Initialize hidden measurement container
   */
  useEffect(() => {
    if (isExportingRef.current) {
      //console.log(`⏸️ Skipping measurement container init - export in progress`);
      return;
    }
    
    // Initialize measurement container immediately and synchronously
    const initMeasureContainer = () => {
      if (!measureContainerRef.current) {
        const measureDiv = document.createElement('div');
        measureDiv.style.cssText = `
          position: absolute;
          left: -9999px;
          visibility: hidden;
          width: ${PAGE_WIDTH - (PAGE_MARGIN * 2)}px;
          font-family: "Times New Roman", serif;
          font-size: 12px;
          line-height: 1.6;
          padding: 0;
          margin: 0;
          white-space: normal;
          word-wrap: break-word;
          box-sizing: border-box;
        `;
        measureDiv.id = 'measure-container';
        
        // Use safe append function
        if (safeAppendChild(document.body, measureDiv)) {
          measureContainerRef.current = measureDiv;
        } else {
          console.warn('⚠️ Failed to append measurement container to body');
        }
      }
    };

    // Initialize immediately
    initMeasureContainer();

    // Load document after measure container is ready
    loadDocument();

    // Cleanup on unmount
    return () => {
      // NUCLEAR OPTION: Skip ALL cleanup if ANY export might be happening
      if ((window as any).isAnyExportRunning || isExportingRef.current) {
        //console.log('⏸️ NUCLEAR: Skipping ALL cleanup - export detected');
        return;
      }

      //console.log('🧹 Starting safe cleanup process...');

      // Clean up measurement container safely with multiple safety checks
      try {
        if (measureContainerRef.current) {
          //console.log('🧹 Attempting to clean up measurement container...');
          if (measureContainerRef.current.parentNode && document.body.contains(measureContainerRef.current)) {
            // Use safe removal method
            if (measureContainerRef.current.parentNode === document.body) {
              safeRemoveChild(document.body, measureContainerRef.current as HTMLElement);
            } else {
              // If parent is not document.body, use the parent directly but safely
              const parent = measureContainerRef.current.parentNode as HTMLElement;
              try {
                parent.removeChild(measureContainerRef.current);
              } catch (removeError) {
                console.warn('⚠️ Failed to remove measurement container from parent:', removeError);
              }
            }
            //console.log('✅ Measurement container removed successfully');
          } else {
            //console.log('ℹ️ Measurement container already detached from DOM');
          }
        }
        measureContainerRef.current = null;
      } catch (error) {
        console.warn('⚠️ Safe measurement container cleanup failed, but continuing:', error);
      }

      // Clean up all timeouts safely
      try {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          //console.log('✅ Save timeout cleared');
        }
        
        if (enterKeyTimeoutRef.current) {
          clearTimeout(enterKeyTimeoutRef.current);
          //console.log('✅ Enter key timeout cleared');
        }
        
        // Reset Enter key processing flag
        isEnterKeyProcessing.current = false;
        //console.log('✅ Enter key processing flag reset');
      } catch (error) {
        console.warn('⚠️ Timeout cleanup failed, but continuing:', error);
      }

      //console.log('🧹 Cleanup process completed');
    };
  }, [loadDocument]); // Note: Don't include isExportingRef in deps since it's a ref

  // ================== COMPONENT INTERFACE ==================

  /**
   * Expose methods to parent component
   */
  useImperativeHandle(ref, () => ({
    exportDocumentData: async (title?: string) => {
      const pages = computePagination(editorHTML);
      return {
        title: title || 'Document',
        reportId: reportId || '',
        pages: pages.map((pageHTML, index) => ({
          id: `page-${index + 1}`,
          content: pageHTML,
          pageNumber: index + 1
        }))
      };
    },
    resetToBlankDocument: () => {
      setEditorHTML('<p><br></p>');
      updateSaveStatus('saved');
    },
    handleClientSideExport: async () => {
      try {
        //console.log('🚀 Starting visual PDF export from editor...');

        // 🎯 NUCLEAR OPTION: Set global flag to prevent ANY cleanup during export
        (window as any).isAnyExportRunning = true;

        // 🎯 IMMEDIATELY set export flag to prevent React updates
        isExportingRef.current = true;
        
        // 🎯 Allow one final overlay position update to ensure overlays are current for export
        allowOneMoreOverlayUpdate.current = true;
        extractOverlayPositions();

        if (!editorRootRef.current) {
          isExportingRef.current = false; // Reset on error
          throw new Error('Editor not ready for export');
        }

        // Generate filename with report title and current date
        const currentDate = new Date();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const year = String(currentDate.getFullYear()).slice(-2); // Get last 2 digits of year
        const dateString = `${month}.${day}.${year}`;

        // Use report title if available, otherwise fallback to "Document"
        const reportName = reportTitle || 'Document';

        // Clean the report name for filename (remove invalid characters)
        const cleanReportName = reportName.replace(/[<>:"/\\|?*]/g, '-');

        const filename = `${cleanReportName} ${dateString}.pdf`;

        // Use the visual export hook that captures exactly what the user sees
        await exportPDF(filename);

      } catch (error) {
        console.error('❌ Visual PDF export failed:', error);
        throw error;
      } finally {
        // 🎯 ALWAYS reset export flags
        isExportingRef.current = false;
        (window as any).isAnyExportRunning = false;
      }
    },
    createFromTemplate: (entries: any[]) => {
      //console.log('🎨 Creating document from template with', entries.length, 'entries');

      // Helper function to escape HTML (moved to top to avoid hoisting issues)
      const escapeHTML = (text: string) => text.replace(/[<>&"']/g, (char: string) => {
        const escape: { [key: string]: string } = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
        return escape[char] || char;
      });

      // Start with empty document
      let templateHTML = '';

      // Add centered title and date at the top if reportTitle is available
      if (reportTitle) {
        // Add the report title, centered and bold but normal text size
        templateHTML += `<p style="text-align: center; font-size: 12px; font-weight: bold; margin: 0 0 0 0; line-height: 1.6;">${escapeHTML(reportTitle)}</p>`;
        
        // Add current date, centered and normal text size with black color
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric', 
          month: 'long',
          day: 'numeric'
        });
        templateHTML += `<p style="text-align: center; font-size: 12px; color: #333333; margin: 0 0 16px 0; line-height: 1.6;">${formattedDate}</p>`;
        
        // No line break paragraph - removed to eliminate spacing
      }

      // Add each entry using the same logic as drag & drop
      entries.forEach((entry, index) => {
        // Add entry title (escape HTML to prevent XSS)
        const escapeHTML = (text: string) => text.replace(/[<>&"']/g, (char: string) => {
          const escape: { [key: string]: string } = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
          return escape[char] || char;
        });

        const entryTitle = escapeHTML(entry.title || entry.name || 'Entry');
        templateHTML += `<p style="margin: 0; line-height: 1.6;"><strong>${entryTitle}</strong></p>`;

        // Add notes if available (escape HTML to prevent XSS)
        if (entry.note && entry.note.trim()) {
          const escapedNote = escapeHTML(entry.note);
          templateHTML += `<p style="margin: 0; line-height: 1.6;">${escapedNote}</p>`;
        }

        // Add images if there are any
        if (entry.images && entry.images.length > 0) {
          templateHTML += '<p style="margin: 0; line-height: 1.6;">';

          // Use consistent standard width for all template images
          const imageWidth = STANDARD_DRAG_IMAGE_WIDTH;

          // Add images with proper metadata support
          entry.images.forEach((image: any, imgIndex: number) => {
            const imageUrl = image.photoURL || image.url;
            const overlayId = `img-${Date.now()}-${index}-${imgIndex}`;
            
            // Check if image has metadata
            let metadataAttrs = '';
            let containerMetadata = '';
            let hasOverlay = 'false';
            
            if (image.metadata) {
              const metadata = image.metadata;
              
              // 🎯 SMART DEFAULT: Only show overlay if image has valid address
              const hasValidMetadata = metadata.address && 
                !metadata.address.startsWith('Location:') && 
                metadata.address !== 'Location Unavailable' &&
                metadata.address.trim().length > 0;
              
              metadataAttrs = `data-metadata-lat="${metadata.latitude || ''}" data-metadata-lng="${metadata.longitude || ''}" data-metadata-address="${metadata.address || ''}" data-metadata-captured="${new Date(metadata.capturedAt || Date.now()).toISOString()}" data-metadata-visible="${hasValidMetadata ? 'true' : 'false'}"`;
              
              const serializedMetadata = {
                latitude: metadata.latitude,
                longitude: metadata.longitude,
                address: metadata.address,
                capturedAt: new Date(metadata.capturedAt || Date.now()).toISOString(),
                visible: hasValidMetadata // Smart default based on metadata quality
              };
              containerMetadata = `data-metadata='${JSON.stringify(serializedMetadata)}'`;
              hasOverlay = 'true';
            }

            // Create container with image (matching the structure from insertFormattedEntry)
            // 🎯 Images default to inline (no float class) - users can change alignment via right-click
            templateHTML += `<span class="inline-image-container" contenteditable="false" data-overlay-id="${overlayId}" data-has-overlay="${hasOverlay}" ${containerMetadata} style="display: inline-block; margin: 0 4px 0 0; vertical-align: top; max-width: 100%; position: relative;"><img src="${imageUrl}" class="inline-image" style="width: ${imageWidth}px; height: auto; max-width: 100%; display: inline-block; vertical-align: top; margin: 0 4px 0 0; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s ease;" data-original-width="${imageWidth}" data-image-url="${imageUrl}" data-image-id="${overlayId}" ${metadataAttrs} crossorigin="anonymous" contenteditable="false" draggable="false"></span>`;

            // Add space between images (except for the last one)
            if (imgIndex < entry.images.length - 1) {
              templateHTML += ' ';
            }
          });

          // Add zero-width space after images for cursor placement (matching insertFormattedEntry)
          templateHTML += '\u200B</p>';
        }

        // Add spacing between entries (except for the last one)
        if (index < entries.length - 1) {
          templateHTML += '<p style="margin: 0; line-height: 1.6;"><br></p>';
        }
      });

      // Add final empty paragraph for cursor
      templateHTML += '<p style="margin: 0; line-height: 1.6;"><br></p>';

      // Set the content
      setEditorHTML(templateHTML);
      updateSaveStatus('unsaved');

      // CRITICAL: Wait for images to load BEFORE rebinding overlays and pagination
      // This is the KEY FIX for the "cuts off after page 1" issue
      const waitForTemplateImages = () => {
        if (!editorRootRef.current) {
          //console.log('⚠️ Editor not ready for template post-processing');
          return;
        }

        //console.log('🖼️ Template created, checking if images are loaded in DOM...');

        // Wait for all images to finish loading
        const images = editorRootRef.current.querySelectorAll('img');
        let allLoaded = true;
        let attemptCount = (waitForTemplateImages as any).__attemptCount || 0;
        (waitForTemplateImages as any).__attemptCount = attemptCount + 1;
        
        if (images.length > 0) {
          //console.log(`📸 Checking ${images.length} template images (attempt ${attemptCount + 1})...`);
          
          images.forEach((img) => {
            const imageEl = img as HTMLImageElement;
            if (!imageEl.complete || imageEl.naturalHeight === 0) {
              //console.log(`⏳ Waiting for image: ${imageEl.src?.substring(0, 50)}...`);
              allLoaded = false;
            } else {
              //console.log(`✓ Image ready: ${imageEl.src?.substring(0, 50)}...`);
            }
          });
        }

        if (!allLoaded && images.length > 0 && attemptCount < 20) {
          // Images still loading, schedule another check (up to 20 attempts = 2 seconds max)
          //console.log(`⏳ Not all images loaded yet, retrying in 100ms (attempt ${attemptCount + 1}/20)...`);
          setTimeout(waitForTemplateImages, 100);
          return;
        }

        if (attemptCount >= 20) {
          //console.log(`⚠️ Timeout waiting for images after ${attemptCount} attempts, proceeding anyway...`);
        } else {
          //console.log(`✅ All ${images.length} template images are ready for pagination`);
        }

        // NOW rebind overlays and trigger pagination with loaded images
        //console.log('🔄 Rebinding overlays and triggering pagination with loaded images...');
        
        if (editorRootRef.current) {
          rebindAllImageOverlays(editorRootRef.current);
          initializeAllImageContextMenus(editorRootRef.current);
          // Force overlay position extraction for template images
          setTimeout(() => extractOverlayPositions(), 100);
        }

        // CRITICAL: Force aggressive layout recalculation NOW that images are loaded
        // This ensures templates display correctly with proper pagination
        //console.log('📄 Forcing pagination with loaded images...');
        setTimeout(() => {
          forceAggressiveLayoutRecalculation();
        }, 100);

        // Clean up attempt counter
        (waitForTemplateImages as any).__attemptCount = 0;
      };

      // Start checking for image load after brief delay to let React render content
      setTimeout(waitForTemplateImages, 200);

      // Auto-save after a delay
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveDocument(templateHTML);
      }, 2000);
    },
    updateOverlayPositions: () => {
      // Force re-extraction of overlay positions
      extractOverlayPositions();
    },
    // Export progress and status methods
    getExportProgress: () => exportProgress,
    isExporting: () => isExporting
  }), [editorHTML, reportId, reportTitle, computePagination, exportPDF, updateSaveStatus, saveDocument, extractOverlayPositions, forceAggressiveLayoutRecalculation, exportProgress, isExporting]);

  // ================== RENDER ==================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      pb: 4
    }}>
      {/* Inline style to absolutely override text selection */}
      <style dangerouslySetInnerHTML={{
        __html: `
          * ::selection {
            background-color: rgba(47, 122, 79, 0.3) !important;
            color: #1E1E1E !important;
          }
          * ::-moz-selection {
            background-color: rgba(47, 122, 79, 0.3) !important;
            color: #1E1E1E !important;
          }
        `
      }} />

      {/* Google Docs Style - Paginated contentEditable with real page boundaries */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>

        {/* Google Docs Style Formatting Toolbar */}
        <Box sx={{
          width: '100%',
          maxWidth: PAGE_WIDTH + 40,
          mb: 1,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          display: 'flex',
          justifyContent: 'center',
          pl: 6  // Push toolbar right to avoid blocking page counter
        }}>
          <FormattingToolbar
            editorRef={editorRootRef}
            disabled={isExporting || isLoading || readOnly}
          />
        </Box>

        {/* Paginated contentEditable with real page containers */}
        <Box
          ref={editorRootRef}
          contentEditable={!readOnly && !isExporting}
          suppressContentEditableWarning
          onInput={handleEditorInput}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleEditorClick}
          className={isDraggingOver ? 'drop-target' : ''}
          sx={{
            position: 'relative',
            zIndex: 2,
            fontSize: '12px',
            fontFamily: '"Times New Roman", serif',
            lineHeight: 1.6,
            color: '#333333', // Visible text now that we have real pages
            cursor: readOnly ? 'default' : 'text',
            outline: 'none',
            backgroundColor: 'transparent',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            '&:focus': {
              outline: 'none'
            },
            // Page styling for divs inside contentEditable
            '& .page-container': {
              width: `${PAGE_WIDTH}px`,
              height: `${PAGE_HEIGHT}px`,
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              position: 'relative',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s ease',
              cursor: readOnly ? 'default' : 'text',
              // Prevent gaps between pages from being clickable
              marginTop: 0,
              marginBottom: 0,
              // Ensure page containers are properly isolated
              contain: 'layout style',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              },
              // Prevent cursor from appearing outside content area
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 1
              }
            },
            '& .page-content': {
              position: 'absolute',
              top: `${PAGE_MARGIN}px`,
              left: `${PAGE_MARGIN}px`,
              right: `${PAGE_MARGIN}px`,
              height: `${PAGE_CONTENT_HEIGHT}px`,
              width: `${PAGE_WIDTH - (PAGE_MARGIN * 2)}px`, // Explicit width constraint
              overflow: 'hidden',
              fontSize: '12px',
              fontFamily: '"Times New Roman", serif',
              lineHeight: 1.6,
              color: '#333333',
              // Ensure content is properly contained
              zIndex: 2,
              // Allow text cursor within content area
              cursor: 'text',
              // Enable text selection within content
              userSelect: 'text',
              WebkitUserSelect: 'text',
              // Critical: Ensure text wraps properly within boundaries
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              boxSizing: 'border-box'
            },
            '& .page-content p': {
              margin: '0', // ✅ SIMPLE FIX - Remove all paragraph margins for Google Docs spacing
              lineHeight: 1.6, // Keep consistent line height
              wordWrap: 'break-word', // Ensure long words break
              overflowWrap: 'break-word', // Modern word breaking
              whiteSpace: 'normal', // Allow normal text wrapping
              maxWidth: '100%', // Constrain to parent width
              boxSizing: 'border-box',
              display: 'block', // Ensure block display for proper paragraph behavior
              minHeight: '1.6em' // Ensure empty paragraphs have same height as filled ones
            },
            '& .page-content p br': {
              lineHeight: 1.6, // Ensure br elements have consistent spacing
              display: 'block' // Force br to respect line-height
            },
            '& .page-content img': {
              maxWidth: '100%',
              height: 'auto'
            },
            // Enhanced image selection styles for better visual feedback
            '& .inline-image': {
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '2px solid transparent',
              borderRadius: '4px',
              '&:hover': {
                border: '2px solid #1a73e8',
                boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)'
              },
              '&.selected': {
                border: '2px solid #1a73e8 !important',
                boxShadow: '0 4px 12px rgba(26, 115, 232, 0.4) !important',
                backgroundColor: 'rgba(26, 115, 232, 0.1)'
              }
            },
            // Drag and drop visual feedback
            '&.drop-target': {
              '& .page-container': {
                border: '2px dashed #1a73e8',
                backgroundColor: 'rgba(26, 115, 232, 0.05)'
              }
            }
          }}

        />


        {/* Page number indicators */}
        {pagesHTML.map((_, index) => (
          <Box
            key={`page-number-${index}`}
            sx={{
              position: 'absolute',
              top: (PAGE_HEIGHT + 20) * index + 70,
              right: '50%',
              transform: 'translateX(50%) translateX(' + (PAGE_WIDTH / 2 + 20) + 'px)',
              zIndex: 3,
              backgroundColor: theme.colors.green,
              color: 'white',
              borderRadius: '50%',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              pointerEvents: 'none'
            }}
          >
            {index + 1}
          </Box>
        ))}

        {/* Status indicator */}
        {pagesHTML.length > 0 && (
          <Box
            sx={{
              marginTop: '40px',
              color: '#666',
              fontSize: '14px',
              textAlign: 'center'
            }}
          >
            {editorHTML.replace(/<[^>]*>/g, '').trim().length > 0 ?
              `${pagesHTML.length} page${pagesHTML.length !== 1 ? 's' : ''}` :
              'Start typing...'
            }
          </Box>
        )}
      </Box>

      {/* Export Progress Indicator - TEMPORARILY DISABLED to prevent DOM conflicts */}
      {false && isExporting && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            boxShadow: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 1000
          }}
        >
          <CircularProgress
            size={16}
            variant={exportProgress > 0 ? "determinate" : "indeterminate"}
            value={exportProgress}
          />
          <Typography variant="body2">
            Exporting PDF... {exportProgress > 0 && `${Math.round(exportProgress)}%`}
          </Typography>
        </Box>
      )}



      {/* Hidden measurement container for accurate pagination */}
      <div
        ref={measureContainerRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: `${PAGE_WIDTH - (PAGE_MARGIN * 2)}px`,
          fontFamily: '"Times New Roman", serif',
          fontSize: '12px',
          lineHeight: 1.6,
          color: '#333333',
          visibility: 'hidden',
          overflow: 'visible',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          margin: 0,
          padding: 0
        }}
      />
    </Box>
  );
});

SimpleDocumentEditor.displayName = 'SimpleDocumentEditor';

export default SimpleDocumentEditor;
